import { useEffect, useRef, useState, type FormEvent, type RefObject, type UIEvent } from 'react';
import { catchAllMailboxIds, retainNewestDraft } from './mailbox-addresses';
import {
  confirmAssistantDraft,
  createMailFolderRule,
  createSelfMailFolderRule,
  createMailAdminHoneypot,
  deleteMailAdminHoneypot,
  deleteMailFolderRule,
  deleteSelfMailFolderRule,
  applyMailRouteApplication,
  canApplyMailRouteApplication,
  loginWebmailSession,
  loadAuthOptions,
  loadIdentityAdminAccounts,
  loadLlmBudget,
  loadMailAdminHoneypots,
  loadMailFolderRules,
  loadSelfMailFolderRules,
  loadSelfServiceAccount,
  loadMailboxMessages,
  loadMailAdminDomainMailboxes,
  loadMailAdminDomainSummary,
  loadMailAdminDomains,
  loadMailRouteApplication,
  loadMailRouteDraft,
  loadMessageDetail,
  loadWebmailSnapshot,
  logoutWebmailSession,
  planMailAdminDomain,
  queryAssistant,
  replaceMailFolderRule,
  replaceSelfMailFolderRule,
  replaceMailRouteDraftDomain,
  replaceMailRouteDraftSendIdentities,
  resetIdentityPassword,
  sendComposeStub,
  isMatchingActiveMailRouteApplication,
  updateLlmBudget,
  ApiError,
  type AuthOptions,
  type AssistantConfirmation,
  type AssistantResult,
  type DomainOnboardingPlan,
  type IdentityAdminAccount,
  type IdentityAdminAccountList,
  type MailAdminHoneypot,
  type LlmBudgetSettings,
  type LlmBudgetSnapshot,
  type MailAdminAddressRoute,
  type MailAdminDomainDetail,
  type MailAdminDomainPage,
  type MailAdminMailbox,
  type MailAdminMailboxPage,
  type MailRouteApplication,
  type MailRouteDraft,
  type MailFolderRule,
  type MailFolderRuleCondition,
  type MailFolderRuleInput,
  type SelfServiceAccount,
  type WebmailSnapshot
} from './api';
import { pendingDomainMailboxes } from './pending-mailboxes';
import { MailboxEditor } from './MailboxEditor';
import type { MessageDetail as MessageDetailModel, MessageSummary, SanitizedHtmlDocument } from './types';

const initialSnapshot: WebmailSnapshot = {
  mailboxes: [],
  identities: [],
  messages: [],
  source: 'api'
};
const fallbackAuthOptions: AuthOptions = {
  passwordLogin: true,
  externalLogin: true,
  externalLoginLabel: 'Sign in with SSO / Passkey'
};
const messagePageSize = 50;
type ConfirmationState = {
  status: 'confirming' | 'confirmed' | 'error';
  detail: string;
};
type ComposeStatus = {
  state: 'idle' | 'sending' | 'sent' | 'error';
  detail: string;
};
type AppView = 'mail' | 'settings' | 'admin';
type AdminStatus = 'idle' | 'loading' | 'ready' | 'forbidden' | 'error';
type RouteApplicationLoadStatus = 'loading' | 'ready' | 'reconnecting' | 'error';
type FolderPageState = {
  loading: boolean;
  hasMore: boolean;
  loaded: number;
};
type AdminSection = 'overview' | 'mailboxes' | 'routing' | 'filing' | 'dns' | 'honeypots';
type MailboxStatusFilter = 'all' | 'ready' | 'attention' | 'pending';
type MailboxSort = 'name' | 'status' | 'routes';
type AdminMailboxRow = {
  mailbox: MailAdminMailbox;
  user: { displayName: string; primaryMailbox: string } | undefined;
  account: IdentityAdminAccount | undefined;
  addresses: MailAdminAddressRoute[];
  aliases: MailAdminAddressRoute[];
  routeCount: number;
  status: 'ready' | 'attention' | 'pending';
};

type MailboxPresentationUser = {
  displayName?: string | null;
};

function mailboxDisplayName(mailbox: MailAdminMailbox, user?: MailboxPresentationUser): string {
  return mailbox.displayName?.trim() || user?.displayName?.trim() || 'Mailbox';
}

function mailboxPrimaryEmail(mailbox: MailAdminMailbox, user?: MailboxPresentationUser): string | null {
  return mailbox.primaryEmail?.trim().toLowerCase() || null;
}

function mailboxOptionLabel(mailbox: MailAdminMailbox, user: MailboxPresentationUser | undefined, index: number): string {
  const name = mailboxDisplayName(mailbox, user);
  const email = mailboxPrimaryEmail(mailbox, user);
  if (email && name !== email) return `${name} · ${email}`;
  return name === 'Mailbox' ? `Mailbox ${index + 1}` : name;
}

function mailboxDestinationLabel(mailboxId: string, mailboxes: MailAdminMailbox[]): string {
  const mailbox = mailboxes.find((candidate) => candidate.id === mailboxId);
  if (!mailbox) return 'Mailbox destination';
  return mailboxOptionLabel(mailbox, undefined, mailboxes.indexOf(mailbox));
}

function mailboxDestinationSummary(mailboxIds: string[], mailboxes: MailAdminMailbox[]): string {
  const labels = mailboxIds.map((mailboxId) => mailboxDestinationLabel(mailboxId, mailboxes));
  return labels.length > 0 ? labels.join(', ') : 'Not configured';
}

function routeMailboxIds(domain: { addresses: MailAdminAddressRoute[]; aliases: MailAdminAddressRoute[]; catchAllMailboxes?: string[]; catchAllMailbox?: string | null }): string[] {
  return Array.from(new Set([
    ...domain.addresses.map((route) => route.mailbox),
    ...domain.aliases.map((route) => route.mailbox),
    ...catchAllMailboxIds(domain)
  ]));
}

export function App() {
  const authError = new URLSearchParams(window.location.search).get('auth_error');
  const externalLoginPath = authError === 'forbidden' ? '/auth/login?prompt=login' : '/auth/login';
  const [snapshot, setSnapshot] = useState<WebmailSnapshot>(initialSnapshot);
  const [isLoading, setIsLoading] = useState(true);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [snapshotAttempt, setSnapshotAttempt] = useState(0);
  const [assistantQuery, setAssistantQuery] = useState('');
  const [assistantResults, setAssistantResults] = useState<AssistantResult[]>([]);
  const [assistantConfirmations, setAssistantConfirmations] = useState<Record<string, ConfirmationState>>({});
  const [assistantStatus, setAssistantStatus] = useState<'idle' | 'running' | 'error'>('idle');
  const [messageDetails, setMessageDetails] = useState<Record<string, MessageDetailModel>>({});
  const [messageDetailStatus, setMessageDetailStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [sessionStatus, setSessionStatus] = useState<'active' | 'logging-out' | 'logged-out' | 'error'>('active');
  const [loginPrimaryEmail, setLoginPrimaryEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginTotpCode, setLoginTotpCode] = useState('');
  const [loginRecoveryCode, setLoginRecoveryCode] = useState('');
  const [loginStatus, setLoginStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [authOptions, setAuthOptions] = useState<AuthOptions>(fallbackAuthOptions);
  const [authOptionsStatus, setAuthOptionsStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [activeMailboxId, setActiveMailboxId] = useState(initialSnapshot.mailboxes[0]?.id ?? '');
  const [activeFolder, setActiveFolder] = useState('INBOX');
  const [selectedMessageId, setSelectedMessageId] = useState(initialSnapshot.messages[0]?.emailId ?? '');
  const [composeFrom, setComposeFrom] = useState(initialSnapshot.identities[0]?.address ?? '');
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [activeView, setActiveView] = useState<AppView>('mail');
  const [identityAdminAccounts, setIdentityAdminAccounts] = useState<IdentityAdminAccountList | null>(null);
  const [folderRules, setFolderRules] = useState<MailFolderRule[]>([]);
  const [adminStatus, setAdminStatus] = useState<AdminStatus>('idle');
  const [selfServiceAccount, setSelfServiceAccount] = useState<SelfServiceAccount | null>(null);
  const [selfFolderRules, setSelfFolderRules] = useState<MailFolderRule[]>([]);
  const [settingsStatus, setSettingsStatus] = useState<AdminStatus>('idle');
  const [folderPages, setFolderPages] = useState<Record<string, FolderPageState>>({});
  const [rawVisibleMessages, setRawVisibleMessages] = useState<Record<string, boolean>>({});
  const [composeStatus, setComposeStatus] = useState<ComposeStatus>({
    state: 'idle',
    detail: 'Backend stub only. No SES send is performed.'
  });
  const { identities, mailboxes, messages } = snapshot;

  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);
    setSnapshotError(null);

    loadAuthOptions()
      .then((options) => {
        if (isCancelled) {
          return;
        }

        setAuthOptions(options);
        setAuthOptionsStatus('ready');
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setAuthOptions(fallbackAuthOptions);
        setAuthOptionsStatus('error');
      });

    loadWebmailSnapshot()
      .then((nextSnapshot) => {
        if (isCancelled) {
          return;
        }

        setSnapshot(nextSnapshot);
        setSnapshotError(null);
        setActiveMailboxId(nextSnapshot.mailboxes[0]?.id ?? '');
        setActiveFolder(orderedFolders(nextSnapshot.mailboxes[0]?.folders ?? {})[0] ?? 'INBOX');
        setSelectedMessageId(nextSnapshot.messages[0]?.emailId ?? '');
        setComposeFrom(nextSnapshot.identities[0]?.address ?? '');
        if (nextSnapshot.mailboxes[0]) {
          const initialFolder = orderedFolders(nextSnapshot.mailboxes[0].folders)[0] ?? 'INBOX';
          setFolderPages({
            [folderKey(nextSnapshot.mailboxes[0].id, initialFolder)]: {
              loading: false,
              hasMore: nextSnapshot.messages.length >= messagePageSize,
              loaded: nextSnapshot.messages.length
            }
          });
        }
      })
      .catch((error: unknown) => {
        if (isCancelled) {
          return;
        }

        setSnapshot(initialSnapshot);
        setActiveMailboxId('');
        setActiveFolder('INBOX');
        setSelectedMessageId('');
        setComposeFrom('');
        setFolderPages({});
        setSnapshotError(snapshotErrorMessage(error));
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [snapshotAttempt]);

  useEffect(() => {
    if (snapshot.source !== 'api') {
      setIdentityAdminAccounts(null);
      setFolderRules([]);
      setAdminStatus('idle');
      return;
    }
    if (activeView !== 'admin') {
      return;
    }

    let isCancelled = false;
    setAdminStatus('loading');
    Promise.allSettled([loadIdentityAdminAccounts(), loadMailFolderRules()])
      .then(([accountsResult, rulesResult]) => {
        if (isCancelled) {
          return;
        }

        if (rulesResult.status === 'rejected') {
          setIdentityAdminAccounts(null);
          setFolderRules([]);
          setAdminStatus(rulesResult.reason instanceof ApiError && rulesResult.reason.status === 403 ? 'forbidden' : 'error');
          return;
        }

        setFolderRules(rulesResult.value);
        setIdentityAdminAccounts(accountsResult.status === 'fulfilled' ? accountsResult.value : null);
        setAdminStatus('ready');
      });

    return () => {
      isCancelled = true;
    };
  }, [activeView, snapshot.source]);

  useEffect(() => {
    if (snapshot.source !== 'api') {
      setSelfServiceAccount(null);
      setSelfFolderRules([]);
      setSettingsStatus('idle');
      return;
    }
    if (activeView !== 'settings') {
      return;
    }

    let isCancelled = false;
    setSettingsStatus('loading');
    Promise.all([loadSelfServiceAccount(), loadSelfMailFolderRules()])
      .then(([account, rules]) => {
        if (isCancelled) {
          return;
        }

        setSelfServiceAccount(account);
        setSelfFolderRules(rules);
        setSettingsStatus('ready');
      })
      .catch((error: unknown) => {
        if (isCancelled) {
          return;
        }

        setSelfServiceAccount(null);
        setSelfFolderRules([]);
        setSettingsStatus(error instanceof ApiError && error.status === 403 ? 'forbidden' : 'error');
      });

    return () => {
      isCancelled = true;
    };
  }, [activeView, snapshot.source]);

  const visibleMessages = messages.filter((message) => {
    return message.mailboxId === activeMailboxId && message.folder === activeFolder;
  });
  const selectedMessage = messages.find((message) => message.emailId === selectedMessageId) ?? visibleMessages[0];
  const selectedDetail = selectedMessage ? messageDetails[selectedMessage.emailId] : undefined;
  const activeMailbox = mailboxes.find((mailbox) => mailbox.id === activeMailboxId);
  const activeFolders = orderedFolders(activeMailbox?.folders ?? {});
  const activeFolderPage = folderPages[folderKey(activeMailboxId, activeFolder)];

  useEffect(() => {
    if (!selectedMessage || snapshot.source !== 'api' || messageDetails[selectedMessage.emailId]) {
      setMessageDetailStatus('idle');
      return;
    }

    let isCancelled = false;
    setMessageDetailStatus('loading');
    loadMessageDetail(selectedMessage.mailboxId, selectedMessage.emailId)
      .then((detail) => {
        if (isCancelled) {
          return;
        }

        setMessageDetails((current) => ({ ...current, [detail.emailId]: detail }));
        setMessageDetailStatus('idle');
      })
      .catch(() => {
        if (!isCancelled) {
          setMessageDetailStatus('error');
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [messageDetails, selectedMessage, snapshot.source]);

  function selectMailbox(mailboxId: string) {
    setActiveMailboxId(mailboxId);
    const mailbox = mailboxes.find((candidate) => candidate.id === mailboxId);
    const nextFolder = orderedFolders(mailbox?.folders ?? {})[0] ?? 'INBOX';
    setActiveFolder(nextFolder);
    const nextMessage = messages.find((message) => message.mailboxId === mailboxId && message.folder === nextFolder);
    setSelectedMessageId(nextMessage?.emailId ?? '');
    loadFolderPage(mailboxId, nextFolder, true);
  }

  function selectFolder(folder: string) {
    setActiveFolder(folder);
    const nextMessage = messages.find((message) => message.mailboxId === activeMailboxId && message.folder === folder);
    setSelectedMessageId(nextMessage?.emailId ?? '');
    loadFolderPage(activeMailboxId, folder, nextMessage ? false : true);
  }

  function loadFolderPage(mailboxId: string, folder: string, reset = false) {
    if (snapshot.source !== 'api' || mailboxId.length === 0 || folder.length === 0) {
      return;
    }

    const key = folderKey(mailboxId, folder);
    const current = folderPages[key] ?? { loading: false, hasMore: true, loaded: 0 };
    if (!reset && (current.loading || !current.hasMore)) {
      return;
    }

    const offset = reset ? 0 : current.loaded;
    setFolderPages((pages) => ({
      ...pages,
      [key]: { ...current, loading: true }
    }));

    loadMailboxMessages(mailboxId, folder, messagePageSize, offset)
      .then((nextMessages) => {
        setSnapshot((currentSnapshot) => ({
          ...currentSnapshot,
          messages: mergeMessages(currentSnapshot.messages, nextMessages, reset ? { mailboxId, folder } : undefined)
        }));
        setFolderPages((pages) => ({
          ...pages,
          [key]: {
            loading: false,
            hasMore: nextMessages.length >= messagePageSize,
            loaded: offset + nextMessages.length
          }
        }));
        if (reset) {
          setSelectedMessageId(nextMessages[0]?.emailId ?? '');
        }
      })
      .catch(() => {
        setFolderPages((pages) => ({
          ...pages,
          [key]: { ...current, loading: false, hasMore: false }
        }));
      });
  }

  function onMessageStackScroll(event: UIEvent<HTMLDivElement>) {
    const target = event.currentTarget;
    const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 180;
    if (nearBottom) {
      loadFolderPage(activeMailboxId, activeFolder);
    }
  }

  function runAssistantSearch() {
    const query = assistantQuery.trim();
    if (query.length === 0) {
      setAssistantResults([]);
      return;
    }

    setAssistantStatus('running');
    queryAssistant(query)
      .then((results) => {
        setAssistantResults(results);
        setAssistantConfirmations({});
        setAssistantStatus('idle');
      })
      .catch(() => {
        setAssistantResults([]);
        setAssistantStatus('error');
      });
  }

  function confirmAssistantResult(result: AssistantResult) {
    if (!result.confirmationId) {
      return;
    }

    setAssistantConfirmations((current) => ({
      ...current,
      [result.id]: { status: 'confirming', detail: 'Confirming server-side validated draft...' }
    }));

    confirmAssistantDraft(result.confirmationId)
      .then((confirmation) => {
        setAssistantConfirmations((current) => ({
          ...current,
          [result.id]: {
            status: 'confirmed',
            detail: confirmationDetail(confirmation)
          }
        }));
      })
      .catch(() => {
        setAssistantConfirmations((current) => ({
          ...current,
          [result.id]: {
            status: 'error',
            detail: 'Confirmation failed or expired. Run the assistant query again to create a new draft.'
          }
        }));
      });
  }

  function submitCompose() {
    const toAddresses = splitAddresses(composeTo);
    if (!activeMailboxId || !composeFrom || toAddresses.length === 0) {
      setComposeStatus({
        state: 'error',
        detail: 'Choose a From identity and at least one recipient.'
      });
      return;
    }

    setComposeStatus({
      state: 'sending',
      detail: 'Submitting to backend stub...'
    });

    sendComposeStub({
      mailboxId: activeMailboxId,
      fromIdentity: composeFrom,
      toAddresses,
      subject: composeSubject,
      bodyText: composeBody
    })
      .then((result) => {
        setComposeStatus({
          state: 'sent',
          detail: `${result.status} ${result.recipientCount} recipient(s) via ${result.sourceField}`
        });
      })
      .catch(() => {
        setComposeStatus({
          state: 'error',
          detail: 'Backend rejected the draft. Check identity, recipients, CSRF, and mailbox scope.'
        });
      });
  }

  function signOut() {
    setSessionStatus('logging-out');
    logoutWebmailSession()
      .then(() => {
        setAssistantResults([]);
        setAssistantConfirmations({});
        setMessageDetails({});
        setSessionStatus('logged-out');
      })
      .catch(() => {
        setSessionStatus('error');
      });
  }

  function signIn() {
    if (loginPrimaryEmail.trim().length === 0 || loginPassword.length === 0) {
      setLoginStatus('error');
      return;
    }

    setLoginStatus('submitting');
    loginWebmailSession(loginPrimaryEmail.trim().toLowerCase(), loginPassword, loginTotpCode, loginRecoveryCode)
      .then(() => loadWebmailSnapshot())
      .then((nextSnapshot) => {
        setSnapshot(nextSnapshot);
        setActiveMailboxId(nextSnapshot.mailboxes[0]?.id ?? '');
        const initialFolder = orderedFolders(nextSnapshot.mailboxes[0]?.folders ?? {})[0] ?? 'INBOX';
        setActiveFolder(initialFolder);
        setSelectedMessageId(nextSnapshot.messages[0]?.emailId ?? '');
        setComposeFrom(nextSnapshot.identities[0]?.address ?? '');
        if (nextSnapshot.mailboxes[0]) {
          setFolderPages({
            [folderKey(nextSnapshot.mailboxes[0].id, initialFolder)]: {
              loading: false,
              hasMore: nextSnapshot.messages.length >= messagePageSize,
              loaded: nextSnapshot.messages.length
            }
          });
        }
        setLoginPassword('');
        setLoginTotpCode('');
        setLoginRecoveryCode('');
        setLoginStatus('idle');
        setSessionStatus('active');
      })
      .catch(() => {
        setLoginStatus('error');
      });
  }

  if (isLoading) {
    return (
      <main className="app-shell">
        <section className="session-closed" aria-label="Connecting to WebMail">
          <p className="eyebrow">Rigelstar Mail</p>
          <h1>Connecting to WebMail.</h1>
          <p>Loading live mailbox data from the authenticated backend.</p>
        </section>
      </main>
    );
  }

  if (snapshotError) {
    return (
      <main className="app-shell">
        <section className="session-closed" aria-label="WebMail unavailable">
          <p className="eyebrow">Rigelstar Mail</p>
          <h1>WebMail unavailable.</h1>
          <p>{snapshotError}</p>
          <button onClick={() => setSnapshotAttempt((attempt) => attempt + 1)} type="button">
            Retry live connection
          </button>
        </section>
      </main>
    );
  }

  if (snapshot.source === 'unauthenticated') {
    const passwordLoginAvailable = authOptionsStatus === 'ready' && authOptions.passwordLogin;
    const externalLoginAvailable = authOptionsStatus !== 'ready' || authOptions.externalLogin;

    return (
      <main className="app-shell">
        <section className="session-closed" aria-label="Sign in to WebMail">
          <p className="eyebrow">Rigelstar Mail</p>
          <h1>Sign in to WebMail.</h1>
          <p>The backend requires an authenticated session before mailbox data is exposed.</p>
          {externalLoginAvailable ? (
            <button onClick={() => { window.location.href = externalLoginPath; }} type="button">
              {authOptions.externalLoginLabel}
            </button>
          ) : null}
          {passwordLoginAvailable ? (
            <>
              <label>
                Primary email
                <input autoCapitalize="none" autoComplete="email" onChange={(event) => setLoginPrimaryEmail(event.target.value)} placeholder="you@example.com" spellCheck={false} type="email" value={loginPrimaryEmail} />
              </label>
              <label>
                Password
                <input onChange={(event) => setLoginPassword(event.target.value)} type="password" value={loginPassword} />
              </label>
              <label>
                TOTP code
                <input
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  onChange={(event) => setLoginTotpCode(event.target.value)}
                  placeholder="Optional unless enabled for this account"
                  type="text"
                  value={loginTotpCode}
                />
              </label>
              <label>
                Recovery code
                <input
                  autoComplete="one-time-code"
                  onChange={(event) => setLoginRecoveryCode(event.target.value)}
                  placeholder="Optional one-time backup code"
                  type="text"
                  value={loginRecoveryCode}
                />
              </label>
              <button disabled={loginStatus === 'submitting'} onClick={signIn} type="button">
                {loginStatus === 'submitting' ? 'Signing in...' : 'Sign in'}
              </button>
              {loginStatus === 'error' ? (
                <small className="session-error">Login failed. Check the primary email, password, TOTP code or recovery code, and backend auth mode.</small>
              ) : null}
            </>
          ) : (
            <small className="auth-mode-note">Password login is disabled for this WebMail deployment. Use SSO / Passkey.</small>
          )}
          {authOptionsStatus === 'error' ? (
            <small className="session-error">Could not load auth options. Refresh before retrying sign-in.</small>
          ) : null}
          {authError ? (
            <small className="session-error">SSO login failed: {authError}. Use the mailbox account when retrying.</small>
          ) : null}
          {authError === 'forbidden' ? (
            <button
              className="secondary-auth-button"
              onClick={() => { window.location.href = '/auth/logout/external'; }}
              type="button"
            >
              Clear Keycloak session
            </button>
          ) : null}
        </section>
      </main>
    );
  }

  if (sessionStatus === 'logged-out') {
    return (
      <main className="app-shell">
        <section className="session-closed" aria-label="Session closed">
          <p className="eyebrow">Rigelstar Mail</p>
          <h1>Session closed.</h1>
          <p>The WebMail session cookie was revoked locally. Reload to bootstrap a fresh local-dev session.</p>
          <button onClick={() => window.location.reload()} type="button">
            Reload WebMail
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <section className="hero-panel" aria-label="Mailbox overview">
        <div className="hero-copy">
          <div>
            <p className="eyebrow">Rigelstar Mail</p>
            <h1>Evidence-first inbox for hostile email.</h1>
          </div>
          <button
            className="session-button"
            disabled={sessionStatus === 'logging-out' || snapshot.source !== 'api'}
            onClick={signOut}
            title={snapshot.source === 'api' ? 'Revoke the local WebMail session cookie' : 'Fixture mode has no server session'}
            type="button"
          >
            {sessionStatus === 'logging-out' ? 'Signing out...' : 'Sign out'}
          </button>
          {sessionStatus === 'error' ? <small className="session-error">Logout failed. Retry before closing the browser.</small> : null}
          <div className="view-switch" aria-label="Workspace view">
            <button className={activeView === 'mail' ? 'active' : ''} onClick={() => setActiveView('mail')} type="button">
              Mail
            </button>
            <button className={activeView === 'settings' ? 'active' : ''} onClick={() => setActiveView('settings')} type="button">
              Settings
            </button>
            <button className={activeView === 'admin' ? 'active' : ''} onClick={() => setActiveView('admin')} type="button">
              Admin
            </button>
            {activeView === 'mail' ? (
              <label className="mailbox-picker">
                <span>Mailbox</span>
                <select onChange={(event) => selectMailbox(event.currentTarget.value)} value={activeMailboxId}>
                  {mailboxes.map((mailbox) => (
                    <option key={mailbox.id} value={mailbox.id}>
                      {mailbox.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        </div>
        <div className="command-card" role="search">
          <span>Ask</span>
          <input
            aria-label="Assistant search"
            onChange={(event) => setAssistantQuery(event.currentTarget.value)}
            placeholder="Find catch-all invoices that look suspicious this week"
            value={assistantQuery}
          />
          <button onClick={runAssistantSearch} type="button">
            Search
          </button>
          <kbd>{isLoading ? 'sync' : snapshot.source}</kbd>
        </div>
        <AssistantResults
          confirmations={assistantConfirmations}
          onConfirm={confirmAssistantResult}
          results={assistantResults}
          status={assistantStatus}
        />
      </section>

      {activeView === 'admin' ? (
        <MailAdminDashboardBounded
          accounts={identityAdminAccounts}
          folderRules={folderRules}
          onAccountsChange={setIdentityAdminAccounts}
          onFolderRulesChange={setFolderRules}
          source={snapshot.source}
          status={adminStatus}
        />
      ) : activeView === 'settings' ? (
        <UserSettingsView
          account={selfServiceAccount}
          folderRules={selfFolderRules}
          mailboxes={mailboxes
            .filter((mailbox) => mailbox.owner === selfServiceAccount?.userId)
            .map((mailbox) => ({ ...mailbox }))}
          onFolderRulesChange={setSelfFolderRules}
          source={snapshot.source}
          status={settingsStatus}
        />
      ) : (
      <section className="mail-grid" aria-label="Webmail workspace">
        <aside className="rail" aria-label="Mailboxes and identities">
          <div className="rail-card identity-card">
            <p className="section-label">Send identities</p>
            {identities.map((identity) => (
              <div className="identity-row" key={identity.address}>
                <strong>{identity.address}</strong>
                <span>{identity.domain}</span>
              </div>
            ))}
          </div>

          <ComposePanel
            body={composeBody}
            fromIdentity={composeFrom}
            identities={identities}
            onBodyChange={setComposeBody}
            onFromChange={setComposeFrom}
            onSubjectChange={setComposeSubject}
            onSubmit={submitCompose}
            onToChange={setComposeTo}
            status={composeStatus}
            subject={composeSubject}
            to={composeTo}
          />
        </aside>

        <section className="message-list" aria-label="Message list">
          <header>
            <div>
              <p className="section-label">{activeMailbox?.label ?? 'Mailbox'}</p>
              <h2>{activeFolder}</h2>
            </div>
            <span className="count-pill">{visibleMessages.length} shown</span>
          </header>

          <div className="folder-strip" aria-label="Folders">
            {activeFolders.map((folder) => (
              <button
                className={folder === activeFolder ? 'folder-chip active' : 'folder-chip'}
                key={folder}
                onClick={() => selectFolder(folder)}
              >
                {folder}
              </button>
            ))}
          </div>

          <div className="message-stack" onScroll={onMessageStackScroll}>
            {visibleMessages.map((message) => (
              <MessageRow
                isSelected={message.emailId === selectedMessage?.emailId}
                key={message.emailId}
                message={message}
                onSelect={setSelectedMessageId}
              />
            ))}
            {activeFolderPage?.loading ? <p className="empty-state">Loading more messages...</p> : null}
            {!activeFolderPage?.hasMore && visibleMessages.length > 0 ? <p className="empty-state">End of folder.</p> : null}
            {visibleMessages.length === 0 && !activeFolderPage?.loading ? <p className="empty-state">No messages in this folder.</p> : null}
          </div>
        </section>

        <section className="message-detail" aria-label="Message detail">
          {selectedMessage ? (
            <MessageDetail
              detail={selectedDetail}
              message={selectedMessage}
              onToggleRaw={() => setRawVisibleMessages((current) => ({ ...current, [selectedMessage.emailId]: !current[selectedMessage.emailId] }))}
              showRaw={Boolean(rawVisibleMessages[selectedMessage.emailId])}
              status={messageDetailStatus}
            />
          ) : (
            <p className="empty-state">Select a message.</p>
          )}
        </section>
      </section>
      )}
    </main>
  );
}

function UserSettingsView({
  account,
  folderRules,
  mailboxes,
  onFolderRulesChange,
  source,
  status
}: {
  account: SelfServiceAccount | null;
  folderRules: MailFolderRule[];
  mailboxes: MailAdminMailbox[];
  onFolderRulesChange: (rules: MailFolderRule[]) => void;
  source: WebmailSnapshot['source'];
  status: AdminStatus;
}) {
  if (source !== 'api') {
    return <section className="admin-shell locked"><p className="section-label">Settings unavailable</p><h2>Connect to the authenticated backend.</h2><p>Fixture mode cannot persist server-side rules or open canonical account settings.</p></section>;
  }
  if (status === 'loading' || status === 'idle') {
    return <section className="admin-shell locked"><p className="section-label">Loading settings</p><h2>Resolving your mailbox ownership.</h2></section>;
  }
  if (status !== 'ready' || !account) {
    return <section className="admin-shell locked"><p className="section-label">Settings unavailable</p><h2>Your account settings could not be loaded.</h2><p>Reload before retrying. No mailbox rules were changed.</p></section>;
  }

  return (
    <section className="admin-shell settings-shell" aria-label="Mailbox settings">
      <header className="admin-header">
        <div><p className="section-label">Your settings</p><h2>Control filing without splitting password authority.</h2></div>
        <span className="admin-badge">Mailbox account</span>
      </header>
      <section className="admin-panel account-security-panel" aria-label="Password and account security">
        <div className="panel-heading"><p className="section-label">Password and security</p><h3>Change your canonical mail password in Keycloak.</h3><p>WebMail never receives the replacement password. Keycloak keeps password policy, re-authentication, federation write-through, and session controls in one place.</p></div>
        {account.accountConsoleUrl ? <a className="account-console-action" href={account.accountConsoleUrl} rel="noreferrer" target="_blank">Open secure password settings</a> : <p className="identity-admin-note">Password self-service is not configured for this deployment. Ask an administrator to enable the Keycloak Account Console link.</p>}
      </section>
      {mailboxes.length > 0 ? (
        <FolderRulePanel mailboxes={mailboxes} onRulesChange={onFolderRulesChange} rules={folderRules} scope="owner" />
      ) : (
        <section className="admin-panel"><div className="panel-heading"><p className="section-label">Server-side filing</p><h3>No owned mailboxes are available.</h3><p>Read access to another mailbox intentionally does not grant permission to change its filters.</p></div></section>
      )}
    </section>
  );
}

function MailAdminDashboardBounded({
  accounts,
  folderRules,
  onAccountsChange,
  onFolderRulesChange,
  source,
  status
}: {
  accounts: IdentityAdminAccountList | null;
  folderRules: MailFolderRule[];
  onAccountsChange: (accounts: IdentityAdminAccountList) => void;
  onFolderRulesChange: (rules: MailFolderRule[]) => void;
  source: WebmailSnapshot['source'];
  status: AdminStatus;
}) {
  const [domainQuery, setDomainQuery] = useState('');
  const [domainPage, setDomainPage] = useState<MailAdminDomainPage | null>(null);
  const [domainLoadStatus, setDomainLoadStatus] = useState<AdminStatus>('idle');
  const [domainLoadError, setDomainLoadError] = useState('');
  const [domainNames, setDomainNames] = useState<string[]>([]);
  const [selectedDomainName, setSelectedDomainName] = useState('');
  const [domainDetail, setDomainDetail] = useState<MailAdminDomainDetail | null>(null);
  const [domainDetailStatus, setDomainDetailStatus] = useState<AdminStatus>('idle');
  const [activeSection, setActiveSection] = useState<AdminSection>('mailboxes');
  const [mailboxQuery, setMailboxQuery] = useState('');
  const [mailboxStatus, setMailboxStatus] = useState<MailboxStatusFilter>('all');
  const [mailboxSort, setMailboxSort] = useState<MailboxSort>('name');
  const [mailboxPage, setMailboxPage] = useState<MailAdminMailboxPage | null>(null);
  const [mailboxLoadStatus, setMailboxLoadStatus] = useState<AdminStatus>('idle');
  const [selectedMailboxId, setSelectedMailboxId] = useState<string | null>(null);
  const [llmBudget, setLlmBudget] = useState<LlmBudgetSnapshot | null>(null);
  const [llmBudgetStatus, setLlmBudgetStatus] = useState<'loading' | 'ready' | 'saving' | 'error'>('loading');
  const [llmBudgetError, setLlmBudgetError] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [routeDraft, installRouteDraft] = useState<MailRouteDraft | null>(null);
  const routeDraftRef = useRef<MailRouteDraft | null>(null);
  function setRouteDraft(incoming: MailRouteDraft): boolean {
    const next = retainNewestDraft(routeDraftRef.current, incoming);
    if (next !== incoming) return false;
    routeDraftRef.current = next;
    installRouteDraft(next);
    return true;
  }
  const [routeDraftStatus, setRouteDraftStatus] = useState<'loading' | 'ready' | 'unavailable' | 'error'>('loading');
  const [routeApplication, setRouteApplication] = useState<MailRouteApplication | null>(null);
  const [routeApplicationStatus, setRouteApplicationStatus] = useState<RouteApplicationLoadStatus>('loading');
  const [routeApplicationError, setRouteApplicationError] = useState('');
  const [adminDataRefresh, setAdminDataRefresh] = useState(0);
  const wizardTriggerRef = useRef<HTMLButtonElement | null>(null);
  const domainRequestGenerationRef = useRef(0);
  const domainDetailRequestGenerationRef = useRef(0);
  const mailboxRequestGenerationRef = useRef(0);
  const accountList = accounts ?? { accounts: [], accountConsoleUrl: null };

  useEffect(() => {
    if (source !== 'api' || status !== 'ready') {
      return;
    }

    let isCancelled = false;
    setLlmBudgetStatus('loading');
    setLlmBudgetError('');
    loadLlmBudget()
      .then((snapshot) => {
        if (!isCancelled) {
          setLlmBudget(snapshot);
          setLlmBudgetStatus('ready');
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setLlmBudget(null);
          setLlmBudgetStatus('error');
          setLlmBudgetError('Budget storage is unavailable. Model calls stay disabled and offline classification continues.');
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [source, status]);

  useEffect(() => {
    if (source !== 'api' || status !== 'ready') {
      return;
    }

    const applicationIsPending = routeApplication?.state === 'queued' || routeApplication?.state === 'applying';
    if (routeApplicationStatus === 'loading' || routeApplicationStatus === 'error' || (!applicationIsPending && routeApplicationStatus !== 'reconnecting')) {
      return;
    }

    let isCancelled = false;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const application = await loadMailRouteApplication();
        if (isCancelled) {
          return;
        }

        setRouteApplication(application);
        setRouteApplicationStatus('ready');
        setRouteApplicationError('');
        if (application.state === 'queued' || application.state === 'applying') {
          timer = window.setTimeout(poll, 1500);
          return;
        }

        if (routeDraft && isMatchingActiveMailRouteApplication(routeDraft.revision, application)) {
          await reloadAdminViewsAfterApplication();
        }
      } catch (error: unknown) {
        if (isCancelled) {
          return;
        }

        if (error instanceof ApiError && error.status === 503) {
          setRouteApplicationStatus('reconnecting');
          setRouteApplicationError('Reconnecting to confirm apply progress. The last request may still be running.');
          timer = window.setTimeout(poll, 1500);
        } else {
          setRouteApplicationStatus('error');
          setRouteApplicationError(routeApplicationErrorMessage(error));
        }
      }
    };

    timer = window.setTimeout(poll, 1500);
    return () => {
      isCancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [routeApplication?.requestedRevision, routeApplication?.state, routeApplicationStatus, routeDraft?.revision, source, status]);

  useEffect(() => {
    if (source !== 'api' || status !== 'ready') {
      return;
    }

    let isCancelled = false;
    setRouteApplication(null);
    setRouteApplicationStatus('loading');
    setRouteApplicationError('');
    loadMailRouteApplication()
      .then((application) => {
        if (!isCancelled) {
          setRouteApplication(application);
          setRouteApplicationStatus('ready');
        }
      })
      .catch((error: unknown) => {
        if (!isCancelled) {
          setRouteApplicationStatus(error instanceof ApiError && error.status === 503 ? 'reconnecting' : 'error');
          setRouteApplicationError(routeApplicationErrorMessage(error));
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [source, status]);

  useEffect(() => {
    if (source !== 'api' || status !== 'ready') {
      return;
    }

    let isCancelled = false;
    setRouteDraftStatus('loading');
    loadMailRouteDraft()
      .then((draft) => {
        if (!isCancelled) {
          setRouteDraft(draft);
          setRouteDraftStatus('ready');
        }
      })
      .catch((error: unknown) => {
        if (!isCancelled) {
          setRouteDraftStatus(error instanceof ApiError && error.status === 503 ? 'unavailable' : 'error');
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [source, status]);

  useEffect(() => {
    if (source !== 'api' || status !== 'ready') {
      return;
    }

    let isCancelled = false;
    const requestGeneration = ++domainRequestGenerationRef.current;
    setDomainLoadStatus('loading');
    setDomainLoadError('');
    setDomainPage(null);
    setDomainDetail(null);
    setMailboxPage(null);
    setSelectedDomainName('');
    setSelectedMailboxId(null);

    loadMailAdminDomains(domainQuery, 25)
      .then((page) => {
        if (isCancelled || requestGeneration !== domainRequestGenerationRef.current) {
          return;
        }

        setDomainPage(page);
        setDomainNames((current) => Array.from(new Set([...current, ...page.domains.map((domain) => domain.domain)])));
        setDomainLoadStatus('ready');
      })
      .catch((error: unknown) => {
        if (isCancelled || requestGeneration !== domainRequestGenerationRef.current) {
          return;
        }

        setDomainLoadStatus(error instanceof Error && error.message.includes('403') ? 'forbidden' : 'error');
        setDomainLoadError('The bounded domain endpoint did not return a route snapshot.');
      });

    return () => {
      isCancelled = true;
    };
  }, [adminDataRefresh, domainQuery, source, status]);

  const domains = domainPage?.domains ?? [];
  const selectedDomainNameResolved = domains.find((domain) => domain.domain === selectedDomainName)?.domain ?? domains[0]?.domain ?? '';
  const selectedDomainSummary = domains.find((domain) => domain.domain === selectedDomainNameResolved);

  useEffect(() => {
    if (!domainPage) {
      return;
    }

    const firstDomain = domainPage.domains[0]?.domain ?? '';
    setSelectedDomainName((current) => domainPage.domains.some((domain) => domain.domain === current) ? current : firstDomain);
    if (!firstDomain) {
      setSelectedMailboxId(null);
      setDomainDetail(null);
      setMailboxPage(null);
    }
  }, [domainPage]);

  useEffect(() => {
    if (!selectedDomainNameResolved) {
      setDomainDetail(null);
      setDomainDetailStatus('idle');
      return;
    }

    let isCancelled = false;
    const requestGeneration = ++domainDetailRequestGenerationRef.current;
    setDomainDetailStatus('loading');
    setDomainDetail(null);
    loadMailAdminDomainSummary(selectedDomainNameResolved)
      .then((detail) => {
        if (!isCancelled && requestGeneration === domainDetailRequestGenerationRef.current) {
          setDomainDetail(detail);
          setDomainDetailStatus('ready');
        }
      })
      .catch((error: unknown) => {
        if (!isCancelled && requestGeneration === domainDetailRequestGenerationRef.current) {
          setDomainDetailStatus(error instanceof Error && error.message.includes('403') ? 'forbidden' : 'error');
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [adminDataRefresh, selectedDomainNameResolved]);

  useEffect(() => {
    if (!selectedDomainNameResolved) {
      setMailboxPage(null);
      setMailboxLoadStatus('idle');
      return;
    }

    let isCancelled = false;
    const requestGeneration = ++mailboxRequestGenerationRef.current;
    setMailboxLoadStatus('loading');
    setMailboxPage(null);
    setSelectedMailboxId(null);
    loadMailAdminDomainMailboxes(selectedDomainNameResolved, mailboxQuery, 25)
      .then((page) => {
        if (!isCancelled && requestGeneration === mailboxRequestGenerationRef.current) {
          setMailboxPage(page);
          setMailboxLoadStatus('ready');
        }
      })
      .catch((error: unknown) => {
        if (!isCancelled && requestGeneration === mailboxRequestGenerationRef.current) {
          setMailboxLoadStatus(error instanceof Error && error.message.includes('403') ? 'forbidden' : 'error');
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [adminDataRefresh, mailboxQuery, selectedDomainNameResolved]);

  useEffect(() => {
    if (mailboxPage && !mailboxPage.mailboxes.some((mailbox) => mailbox.id === selectedMailboxId)
      && !pendingDomainMailboxes(routeDraft, domainDetail?.domain === selectedDomainNameResolved ? domainDetail : null, mailboxQuery).some((mailbox) => mailbox.id === selectedMailboxId)) {
      setSelectedMailboxId(null);
    }
  }, [mailboxPage, selectedMailboxId, routeDraft, domainDetail, selectedDomainNameResolved, mailboxQuery]);

  if (source !== 'api') {
    return (
      <section className="admin-shell locked" aria-label="Mail admin unavailable">
        <p className="section-label">Mail admin</p>
        <h2>Connect to the backend to inspect route policy.</h2>
        <p>Fixture mode does not expose domain routing, catch-all policy, LDAP users, or live mail transport status.</p>
      </section>
    );
  }

  if (status === 'loading') {
    return (
      <section className="admin-shell locked" aria-label="Mail admin loading">
        <p className="section-label">Mail admin</p>
        <h2>Loading control-plane access.</h2>
        <p>Reading bounded route endpoints plus the existing account and filing-rule controls. No Stalwart, SES, AWS, or DNS mutations are performed.</p>
      </section>
    );
  }

  if (status === 'forbidden') {
    return (
      <section className="admin-shell locked" aria-label="Mail admin locked">
        <p className="section-label">Mail admin</p>
        <h2>Admin access is locked.</h2>
        <p>Set <code>MAILER_WEBMAIL_ADMIN_USERS</code> to a comma-separated list of route users that can view the global mail control plane.</p>
      </section>
    );
  }

  if (status === 'error') {
    return (
      <section className="admin-shell locked" aria-label="Mail admin error">
        <p className="section-label">Mail admin</p>
        <h2>Control-plane access failed.</h2>
        <p>The filing-rule endpoint did not return. Check server logs before adding write actions.</p>
      </section>
    );
  }

  if (!domainPage && (domainLoadStatus === 'idle' || domainLoadStatus === 'loading')) {
    return (
      <section className="admin-shell locked" aria-label="Mail admin domain loading">
        <p className="section-label">Mail admin</p>
        <h2>Loading bounded domain index.</h2>
        <p>Searching domains through the paginated admin endpoint. No legacy overview is requested.</p>
      </section>
    );
  }

  if (domainLoadStatus === 'forbidden') {
    return (
      <section className="admin-shell locked" aria-label="Mail admin locked">
        <p className="section-label">Mail admin</p>
        <h2>Admin access is locked.</h2>
        <p>The bounded domain endpoint rejected this session. Re-authenticate before inspecting route policy.</p>
      </section>
    );
  }

  if (domainLoadStatus === 'error') {
    return (
      <section className="admin-shell locked" aria-label="Mail admin error">
        <p className="section-label">Mail admin</p>
        <h2>Domain index failed.</h2>
        <p>{domainLoadError || 'The backend did not return a bounded domain page.'}</p>
      </section>
    );
  }

  const selectedDomain = domainDetail?.domain === selectedDomainNameResolved ? domainDetail : null;
  const mailboxRows: AdminMailboxRow[] = (mailboxPage?.mailboxes ?? []).map((mailbox) => {
    const account = accountList.accounts.find((candidate) => candidate.userId === mailbox.owner || candidate.mailboxIds.includes(mailbox.id));
    const addresses = selectedDomain?.addresses.filter((route) => route.mailbox === mailbox.id) ?? [];
    const aliases = selectedDomain?.aliases.filter((route) => route.mailbox === mailbox.id) ?? [];
    return {
      mailbox,
      user: account,
      account,
      addresses,
      aliases,
      routeCount: addresses.length + aliases.length + (selectedDomain && catchAllMailboxIds(selectedDomain).includes(mailbox.id) ? 1 : 0),
      status: account?.status === 'ready' ? 'ready' : 'attention'
    };
  });
  const pendingRows: AdminMailboxRow[] = pendingDomainMailboxes(routeDraft, selectedDomain, mailboxQuery).map((mailbox) => {
    const policy = routeDraft?.domains.find((domain) => domain.domain === selectedDomain?.domain);
    const addresses = policy?.addresses.filter((route) => route.mailbox === mailbox.id) ?? [];
    const aliases = policy?.aliases.filter((route) => route.mailbox === mailbox.id) ?? [];
    const user = routeDraft?.users.find((candidate) => candidate.id === mailbox.owner);
    return { mailbox, user, account: undefined, addresses, aliases,
      routeCount: addresses.length + aliases.length + (policy && catchAllMailboxIds(policy).includes(mailbox.id) ? 1 : 0),
      status: 'pending' };
  });
  const visibleMailboxRows = [...mailboxRows, ...pendingRows.filter((row) => !mailboxRows.some((active) => active.mailbox.id === row.mailbox.id))];
  const filteredRows = visibleMailboxRows.filter((row) => mailboxStatus === 'all' || row.status === mailboxStatus);
  const sortedRows = [...filteredRows].sort((left, right) => {
    if (mailboxSort === 'routes') {
      return right.routeCount - left.routeCount || left.mailbox.id.localeCompare(right.mailbox.id);
    }
    if (mailboxSort === 'status') {
      return left.status.localeCompare(right.status) || left.mailbox.id.localeCompare(right.mailbox.id);
    }
    return mailboxDisplayName(left.mailbox, left.user).localeCompare(mailboxDisplayName(right.mailbox, right.user));
  });
  const selectedRow = visibleMailboxRows.find((row) => row.mailbox.id === selectedMailboxId);
  const routeCount = selectedDomain ? selectedDomain.exactRouteCount + selectedDomain.aliasCount + catchAllMailboxIds(selectedDomain).length : 0;
  const selectedDomainMailboxIds = new Set(mailboxRows.map((row) => row.mailbox.id));
  const selectedDomainRules = folderRules.filter((rule) => selectedDomainMailboxIds.has(rule.mailbox_id));

  function chooseDomain(domain: string) {
    const domainChanged = domain !== selectedDomainNameResolved;
    const mailboxQueryChanged = mailboxQuery !== '';
    if (domainChanged) {
      domainDetailRequestGenerationRef.current += 1;
      mailboxRequestGenerationRef.current += 1;
    } else if (mailboxQueryChanged) {
      mailboxRequestGenerationRef.current += 1;
    }
    setSelectedDomainName(domain);
    setActiveSection('overview');
    setMailboxQuery('');
    setSelectedMailboxId(null);
  }

  function chooseSection(section: AdminSection) {
    setActiveSection(section);
  }

  function openWizard(trigger: HTMLButtonElement) {
    wizardTriggerRef.current = trigger;
    setWizardOpen(true);
  }

  function replaceScopedRules(nextRules: MailFolderRule[]) {
    onFolderRulesChange([
      ...folderRules.filter((rule) => !selectedDomainMailboxIds.has(rule.mailbox_id)),
      ...nextRules
    ]);
  }

  async function reloadAdminViewsAfterApplication() {
    const [draftResult, accountsResult, rulesResult] = await Promise.allSettled([
      loadMailRouteDraft(),
      loadIdentityAdminAccounts(),
      loadMailFolderRules()
    ]);

    if (draftResult.status === 'fulfilled') {
      setRouteDraft(draftResult.value);
      setRouteDraftStatus('ready');
    } else {
      setRouteDraftStatus(draftResult.reason instanceof ApiError && draftResult.reason.status === 503 ? 'unavailable' : 'error');
    }
    if (accountsResult.status === 'fulfilled') {
      onAccountsChange(accountsResult.value);
    }
    if (rulesResult.status === 'fulfilled') {
      onFolderRulesChange(rulesResult.value);
    }
    setAdminDataRefresh((current) => current + 1);
  }

  function installRouteApplication(application: MailRouteApplication) {
    setRouteApplication(application);
    setRouteApplicationStatus('ready');
    setRouteApplicationError('');
    if (routeDraft && isMatchingActiveMailRouteApplication(routeDraft.revision, application)) {
      void reloadAdminViewsAfterApplication();
    }
  }

  async function reloadRouteApplication() {
    setRouteApplicationStatus('loading');
    setRouteApplicationError('');
    try {
      installRouteApplication(await loadMailRouteApplication());
    } catch (error: unknown) {
      setRouteApplicationStatus(error instanceof ApiError && error.status === 503 ? 'reconnecting' : 'error');
      setRouteApplicationError(routeApplicationErrorMessage(error));
    }
  }

  async function requestRouteApplication() {
    if (!routeDraft || routeApplicationStatus === 'loading' || routeApplicationStatus === 'reconnecting' || !canApplyMailRouteApplication(routeDraft.revision, routeApplication)) {
      return;
    }

    setRouteApplicationStatus('loading');
    setRouteApplicationError('');
    try {
      installRouteApplication(await applyMailRouteApplication(routeDraft.revision));
    } catch (error: unknown) {
      setRouteApplicationStatus(error instanceof ApiError && error.status === 503 ? 'reconnecting' : 'error');
      setRouteApplicationError(routeApplicationErrorMessage(error));
    }
  }

  async function reloadRouteDraft() {
    setRouteDraftStatus('loading');
    try {
      const draft = await loadMailRouteDraft();
      setRouteDraft(draft);
      setRouteDraftStatus('ready');
    } catch (error: unknown) {
      setRouteDraftStatus(error instanceof ApiError && error.status === 503 ? 'unavailable' : 'error');
    }
  }

  async function saveLlmBudget(settings: LlmBudgetSettings) {
    setLlmBudgetStatus('saving');
    setLlmBudgetError('');
    try {
      const snapshot = await updateLlmBudget(settings);
      setLlmBudget(snapshot);
      setLlmBudgetStatus('ready');
    } catch (error: unknown) {
      setLlmBudgetStatus('error');
      setLlmBudgetError(error instanceof ApiError && error.status === 409
        ? 'Budget settings changed in another session. Reload the page before retrying.'
        : 'Budget settings were not saved. Model calls remain governed by the last stored limit.');
    }
  }

  function loadMoreDomains() {
    if (!domainPage?.nextCursor || domainLoadStatus === 'loading') {
      return;
    }

    const requestGeneration = ++domainRequestGenerationRef.current;
    const query = domainQuery;
    const cursor = domainPage.nextCursor;
    setDomainLoadStatus('loading');
    loadMailAdminDomains(query, 25, cursor)
      .then((nextPage) => {
        if (requestGeneration !== domainRequestGenerationRef.current) {
          return;
        }

        setDomainPage((current) => current ? {
          domains: [...current.domains, ...nextPage.domains],
          nextCursor: nextPage.nextCursor
        } : nextPage);
        setDomainNames((current) => Array.from(new Set([...current, ...nextPage.domains.map((domain) => domain.domain)])));
        setDomainLoadStatus('ready');
      })
      .catch((error: unknown) => {
        if (requestGeneration !== domainRequestGenerationRef.current) {
          return;
        }

        setDomainLoadStatus(error instanceof Error && error.message.includes('403') ? 'forbidden' : 'error');
        setDomainLoadError('The next bounded domain page could not be loaded.');
      });
  }

  function loadMoreMailboxes() {
    if (!selectedDomainNameResolved || !mailboxPage?.nextCursor || mailboxLoadStatus === 'loading') {
      return;
    }

    const requestGeneration = ++mailboxRequestGenerationRef.current;
    const domain = selectedDomainNameResolved;
    const query = mailboxQuery;
    const cursor = mailboxPage.nextCursor;
    setMailboxLoadStatus('loading');
    loadMailAdminDomainMailboxes(domain, query, 25, cursor)
      .then((nextPage) => {
        if (requestGeneration !== mailboxRequestGenerationRef.current) {
          return;
        }

        setMailboxPage((current) => current ? {
          mailboxes: [...current.mailboxes, ...nextPage.mailboxes],
          nextCursor: nextPage.nextCursor
        } : nextPage);
        setMailboxLoadStatus('ready');
      })
      .catch(() => {
        if (requestGeneration === mailboxRequestGenerationRef.current) {
          setMailboxLoadStatus('error');
        }
      });
  }

  async function checkDomainAvailability(domain: string): Promise<'duplicate' | 'available' | 'unavailable'> {
    const normalized = domain.trim().toLowerCase();
    if (domainNames.some((candidate) => candidate.toLowerCase() === normalized)) {
      return 'duplicate';
    }

    try {
      await loadMailAdminDomainSummary(normalized);
      return 'duplicate';
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 404) {
        return 'available';
      }
      return 'unavailable';
    }
  }

  return (
    <section className="admin-shell admin-shell-domain-first" aria-label="Mail admin dashboard">
      <header className="admin-header admin-domain-header">
        <div>
          <p className="section-label">Control plane / Mail admin</p>
          <h2>Keep every domain legible.</h2>
          <p className="admin-lede">Choose a domain to inspect its mailboxes, route policy, filing rules, and provider handoff without mixing tenants.</p>
        </div>
        <div className="admin-header-actions">
          <span className="admin-badge">{routeDraft ? `draft revision ${routeDraft.revision}` : 'read-only bounded route snapshot'}</span>
          <div className="admin-header-links">
            {accounts && accountList.accountConsoleUrl ? <a className="account-console-link" href={accountList.accountConsoleUrl} rel="noreferrer" target="_blank">My password &amp; passkeys</a> : accounts ? null : <span className="admin-inline-note">Account controls unavailable</span>}
            <button className="admin-primary-action" onClick={(event) => openWizard(event.currentTarget)} type="button">+ New domain</button>
          </div>
        </div>
      </header>

      <LlmBudgetPanel
        error={llmBudgetError}
        onSave={saveLlmBudget}
        snapshot={llmBudget}
        status={llmBudgetStatus}
      />

      <RouteDraftStatusPanel
        application={routeApplication}
        applicationError={routeApplicationError}
        applicationStatus={routeApplicationStatus}
        draft={routeDraft}
        onApply={() => void requestRouteApplication()}
        onReload={reloadRouteDraft}
        onReloadApplication={() => void reloadRouteApplication()}
        status={routeDraftStatus}
      />

      <div className="admin-domain-layout">
        <aside className="domain-sidebar" aria-label="Domains and admin sections">
          <div className="domain-sidebar-heading">
            <div><p className="section-label">Domains</p><strong>{domains.length}{domainPage?.nextCursor ? '+' : ''} loaded</strong></div>
            <button className="sidebar-add-button" onClick={(event) => openWizard(event.currentTarget)} type="button">+ New domain</button>
          </div>
          <label className="domain-search"><span>Search domains</span><input aria-label="Search domains" onChange={(event) => { domainRequestGenerationRef.current += 1; domainDetailRequestGenerationRef.current += 1; mailboxRequestGenerationRef.current += 1; setDomainQuery(event.currentTarget.value); }} placeholder="Find a domain" type="search" value={domainQuery} /></label>
          <div className="admin-global-nav" aria-label="Global admin controls">
            <p className="section-label">Global controls</p>
            <button
              className={activeSection === 'honeypots' ? 'admin-global-nav-button active' : 'admin-global-nav-button'}
              onClick={() => chooseSection('honeypots')}
              type="button"
            >
              <span className="admin-global-nav-mark" aria-hidden="true">!</span>
              <span className="admin-global-nav-copy"><strong>Honeypots</strong><small>All configured domains</small></span>
            </button>
          </div>
          <nav className="domain-nav" aria-label="Configured domains">
            {domains.length > 0 ? domains.map((domain) => {
              const isSelected = domain.domain === selectedDomainNameResolved;
              const domainRouteCount = domain.exactRouteCount + domain.aliasCount + catchAllMailboxIds(domain).length;
              return (
                <div className={isSelected ? 'domain-nav-group selected' : 'domain-nav-group'} key={domain.domain}>
                  <button className="domain-nav-button" onClick={() => chooseDomain(domain.domain)} type="button">
                    <span className="domain-mark" aria-hidden="true">@</span>
                    <span className="domain-nav-copy"><strong>{domain.domain}</strong><small>{domainRouteCount} routes · {domain.mailboxCount} mailboxes</small></span>
                    <span className={catchAllMailboxIds(domain).length > 0 ? 'domain-health healthy' : 'domain-health'} aria-label={catchAllMailboxIds(domain).length > 0 ? 'Catch-all configured' : 'Catch-all missing'} />
                  </button>
                  {isSelected ? <div className="domain-subnav" aria-label={`${domain.domain} sections`}>
                    <AdminSectionButton active={activeSection === 'overview'} label="Overview" onClick={() => chooseSection('overview')} />
                    <AdminSectionButton active={activeSection === 'mailboxes'} label="Mailboxes" onClick={() => chooseSection('mailboxes')} />
                    <AdminSectionButton active={activeSection === 'routing'} label="Address routing" onClick={() => chooseSection('routing')} />
                    <AdminSectionButton active={activeSection === 'filing'} label="Filing rules" onClick={() => chooseSection('filing')} />
                    <AdminSectionButton active={activeSection === 'dns'} label="Delivery & DNS" onClick={() => chooseSection('dns')} />
                  </div> : null}
                </div>
              );
            }) : <p className="sidebar-empty">No domains match that search. The effective selection has been cleared.</p>}
          </nav>
          {domainPage?.nextCursor ? <button className="sidebar-load-more" disabled={domainLoadStatus === 'loading'} onClick={loadMoreDomains} type="button">{domainLoadStatus === 'loading' ? 'Loading domains...' : 'Load more domains'}</button> : null}
          <div className="domain-sidebar-footer"><span className="snapshot-dot" aria-hidden="true" /><span>Bounded local policy snapshot</span></div>
        </aside>

        {activeSection === 'honeypots' ? (
          <section className="admin-content admin-content-global" aria-label="Global honeypot administration">
            <header className="admin-content-header">
              <div><p className="section-label">Global control plane</p><h3>Honeypot addresses</h3></div>
              <div className="admin-content-meta"><span className="snapshot-badge">Across all domains</span><small>Address controls only</small></div>
            </header>
            <HoneypotPanel />
          </section>
        ) : (
        <section className="admin-content" aria-label={selectedDomainSummary ? `${selectedDomainSummary.domain} admin view` : 'Domain admin view'}>
          <header className="admin-content-header">
            <div><p className="section-label">{selectedDomainSummary ? selectedDomainSummary.domain : 'No domain selected'}</p><h3>{selectedDomainSummary ? adminSectionTitle(activeSection) : 'Add a domain to begin'}</h3></div>
            {selectedDomainSummary ? <div className="admin-content-meta"><span className="snapshot-badge">Synced from bounded API</span><small>{selectedDomainSummary.mailboxCount} active mailbox{selectedDomainSummary.mailboxCount === 1 ? '' : 'es'} · {selectedDomainSummary.exactRouteCount + selectedDomainSummary.aliasCount} direct route{selectedDomainSummary.exactRouteCount + selectedDomainSummary.aliasCount === 1 ? '' : 's'}</small></div> : null}
          </header>

          {!selectedDomainSummary ? <EmptyAdminState onOpenWizard={openWizard} /> : !selectedDomain ? <section className="admin-loading-state" aria-live="polite"><p className="section-label">Domain policy</p><h4>{domainDetailStatus === 'error' ? 'Domain detail failed.' : 'Loading domain policy.'}</h4><p>The summary and route entries are read through the selected-domain endpoint.</p></section> : activeSection === 'overview' ? <AdminDomainOverview domain={selectedDomain} mailboxCount={selectedDomain.mailboxCount} mailboxes={mailboxPage?.mailboxes ?? []} routeCount={routeCount} /> : activeSection === 'mailboxes' ? (
            <div className="admin-mailbox-view">
              {pendingRows.length > 0 ? <p role="status">{pendingRows.length} new mailbox{pendingRows.length === 1 ? ' is' : 'es are'} saved below as Pending apply. Apply the route draft to activate mail delivery.</p> : null}
              {routeDraft ? <MailboxEditor key={selectedDomain.domain} domain={selectedDomain.domain} draft={routeDraft} onDraftChange={setRouteDraft} /> : null}
              <div className="mailbox-toolbar">
                <label className="mailbox-search"><span>Search mailboxes</span><input aria-label="Search mailboxes" onChange={(event) => { mailboxRequestGenerationRef.current += 1; setMailboxQuery(event.currentTarget.value); }} placeholder="Name or primary email" type="search" value={mailboxQuery} /></label>
                <label className="mailbox-control"><span>Status</span><select aria-label="Filter mailboxes by status" onChange={(event) => setMailboxStatus(event.currentTarget.value as MailboxStatusFilter)} value={mailboxStatus}><option value="all">All statuses</option><option value="ready">Ready</option><option value="attention">Needs attention</option><option value="pending">Pending apply</option></select></label>
                <label className="mailbox-control"><span>Sort</span><select aria-label="Sort mailboxes" onChange={(event) => setMailboxSort(event.currentTarget.value as MailboxSort)} value={mailboxSort}><option value="name">Name</option><option value="status">Status</option><option value="routes">Routes</option></select></label>
              </div>
              <div className="mailbox-table-frame">
                <table className="mailbox-table"><caption className="sr-only">Mailboxes for {selectedDomain.domain}</caption><thead><tr><th scope="col">Mailbox</th><th scope="col">Description</th><th scope="col">Alternative emails</th><th scope="col">Status</th><th scope="col"><span className="sr-only">Inspect</span></th></tr></thead><tbody>
                  {sortedRows.map((row) => <tr className={row.mailbox.id === selectedMailboxId ? 'selected' : ''} key={row.mailbox.id}>
                    <td><button className="mailbox-name-button" onClick={() => setSelectedMailboxId(row.mailbox.id)} type="button"><span className="table-primary-cell"><span className="mailbox-avatar" aria-hidden="true">{mailboxDisplayName(row.mailbox, row.user).slice(0, 1).toUpperCase()}</span><span><strong>{mailboxDisplayName(row.mailbox, row.user)}</strong><small>{mailboxPrimaryEmail(row.mailbox, row.user) ?? 'Primary email unavailable'}</small></span></span></button></td>
                    <td>{row.mailbox.description || '—'}</td><td><ul className="mailbox-alternative-summary">{[...row.addresses, ...row.aliases].filter((route) => route.address !== mailboxPrimaryEmail(row.mailbox, row.user)).map((route) => <li key={route.address}>{route.address}</li>)}</ul></td><td><span className={`mailbox-status ${row.status}`}>{row.status === 'pending' ? 'Pending apply' : row.status === 'ready' ? 'Ready' : 'Needs attention'}</span></td><td><button className="table-inspect-button" onClick={() => setSelectedMailboxId(row.mailbox.id)} type="button">Inspect</button></td>
                  </tr>)}
                </tbody></table>
                {mailboxLoadStatus === 'error' ? <p className="table-empty">Mailbox page failed to load. Try the search again.</p> : null}
                {mailboxLoadStatus !== 'error' && sortedRows.length === 0 ? <p className="table-empty">No mailboxes match those filters. The effective mailbox selection has been cleared.</p> : null}
              </div>
              <div className="mailbox-pagination" aria-label="Mailbox pagination"><span>{sortedRows.length} shown (active page and saved new mailboxes){mailboxPage?.nextCursor ? ' · more available' : ''}</span><button disabled={!mailboxPage?.nextCursor || mailboxLoadStatus === 'loading'} onClick={loadMoreMailboxes} type="button">{mailboxLoadStatus === 'loading' ? 'Loading...' : 'Load more'}</button></div>
              {selectedRow ? <AdminMailboxInspector key={selectedRow.mailbox.id} row={selectedRow} /> : <p className="inspector-hint">Select a mailbox to inspect ownership, routes, and gated actions.</p>}
            </div>
          ) : activeSection === 'routing' ? <><AdminRoutingView domain={selectedDomain} mailboxes={mailboxPage?.mailboxes ?? []} />{routeDraft ? <RouteDraftEditor domain={selectedDomain.domain} draft={routeDraft} onDraftChange={setRouteDraft} /> : null}</> : activeSection === 'filing' ? (mailboxRows.length > 0 ? <FolderRulePanel key={`filing-${selectedDomainNameResolved}`} mailboxes={mailboxRows.map((row) => row.mailbox)} onRulesChange={replaceScopedRules} rules={selectedDomainRules} /> : <EmptyAdminState title="No mailbox routes to file" />) : <AdminDeliveryDnsView domain={selectedDomain} />}
        </section>
        )}
      </div>
      {wizardOpen ? <NewDomainWizard domains={domainNames} onCheckDomain={checkDomainAvailability} onClose={() => setWizardOpen(false)} returnFocusRef={wizardTriggerRef} /> : null}
    </section>
  );
}

function RouteDraftStatusPanel({
  application,
  applicationError,
  applicationStatus,
  draft,
  onApply,
  onReload,
  onReloadApplication,
  status
}: {
  application: MailRouteApplication | null;
  applicationError: string;
  applicationStatus: RouteApplicationLoadStatus;
  draft: MailRouteDraft | null;
  onApply: () => void;
  onReload: () => Promise<void>;
  onReloadApplication: () => void;
  status: 'loading' | 'ready' | 'unavailable' | 'error';
}) {
  if (status === 'unavailable') {
    return <section className="route-draft-status unavailable"><div className="route-draft-status-copy"><p className="section-label">Route editing</p><h3>Draft editing is unavailable.</h3><p>The current mail setup remains readable. Draft changes are unavailable until server storage is configured.</p></div></section>;
  }

  if (status === 'loading') {
    return <section className="route-draft-status"><div className="route-draft-status-copy"><p className="section-label">Route editing</p><h3>Loading the draft revision.</h3></div></section>;
  }

  if (status === 'error' || !draft) {
    return <section className="route-draft-status error"><div className="route-draft-status-copy"><p className="section-label">Route editing</p><h3>The draft could not be loaded.</h3><p>No route write is available until the latest revision is reloaded.</p></div><button onClick={() => void onReload()} type="button">Reload draft</button></section>;
  }

  const applicationIsPending = application?.state === 'queued' || application?.state === 'applying';
  const applicationIsActive = isMatchingActiveMailRouteApplication(draft.revision, application);
  const canApply = applicationStatus === 'ready' && canApplyMailRouteApplication(draft.revision, application);
  let applicationMessage = 'Checking whether this draft can be applied.';
  if (applicationStatus === 'reconnecting') {
    applicationMessage = 'Reconnecting to confirm apply progress. The last request may still be running.';
  } else if (applicationStatus === 'error') {
    applicationMessage = applicationError || 'Apply status could not be loaded. Reload status before applying.';
  } else if (application?.available === false) {
    applicationMessage = 'Applying is unavailable on this server. Draft changes remain saved but will not change mail delivery.';
  } else if (applicationIsActive) {
    applicationMessage = `Revision ${draft.revision} is active. Mail services are using this applied revision.`;
  } else if (application?.state === 'queued') {
    applicationMessage = `Revision ${application.requestedRevision ?? draft.revision} is queued. The page will check again automatically.`;
  } else if (application?.state === 'applying') {
    applicationMessage = `Revision ${application.requestedRevision ?? draft.revision} is applying. Mail services may reconnect briefly while they restart.`;
  } else if (application?.state === 'failed') {
    applicationMessage = application.error || 'The server reported a failed apply. Review the draft and try again.';
  } else if (application && application.activeRevision === draft.revision) {
    applicationMessage = `Revision ${draft.revision} is already current. Reload apply status before making another change.`;
  } else if (applicationStatus === 'ready') {
    applicationMessage = `Revision ${draft.revision} is ready to apply. Nothing changes mail delivery until you apply it.`;
  }

  const applicationClass = applicationIsActive ? 'active' : applicationIsPending ? 'pending' : application?.state === 'failed' || applicationStatus === 'error' ? 'failed' : '';
  return (
    <section className="route-draft-status ready">
      <div className="route-draft-status-copy">
        <p className="section-label">{applicationIsActive ? 'Active route configuration' : 'Unapplied route draft'}</p>
        <h3>Revision {draft.revision} is editable and ready for review.</h3>
        <p>Changes are checked against the latest draft and validated as one complete route configuration. Mail delivery keeps using the current revision until you apply this draft.</p>
        <div className={`route-application-state ${applicationClass}`} aria-live="polite">
          <strong>Apply status</strong>
          <span>{applicationMessage}</span>
          {application?.error && application.state !== 'failed' && applicationStatus !== 'error' ? <small>{application.error}</small> : null}
          {applicationError && applicationStatus === 'reconnecting' ? <small>{applicationError}</small> : null}
        </div>
      </div>
      <div className="route-draft-actions">
        <button onClick={() => void onReload()} type="button">Reload revision</button>
        {(applicationStatus === 'error' || applicationStatus === 'reconnecting') ? <button onClick={onReloadApplication} type="button">Reload apply status</button> : null}
        {application?.available ? <button className="route-application-primary" disabled={!canApply || applicationIsPending} onClick={onApply} type="button">{applicationIsPending ? 'Applying...' : applicationIsActive ? 'Applied' : application?.state === 'failed' ? `Retry revision ${draft.revision}` : `Apply revision ${draft.revision}`}</button> : null}
      </div>
    </section>
  );
}

function RouteDraftEditor({ domain, draft, onDraftChange }: { domain: string; draft: MailRouteDraft; onDraftChange: (draft: MailRouteDraft) => void }) {
  const draftDomain = draft.domains.find((candidate) => candidate.domain === domain);
  const [addressLines, setAddressLines] = useState('');
  const [aliasLines, setAliasLines] = useState('');
  const [catchAllMailboxes, setCatchAllMailboxes] = useState<string[]>([]);
  const [loginPolicy, setLoginPolicy] = useState<'explicit' | 'routed'>('explicit');
  const [senderPolicy, setSenderPolicy] = useState<'explicit' | 'routed'>('explicit');
  const [routeStatus, setRouteStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [routeMessage, setRouteMessage] = useState('');
  const [identityUser, setIdentityUser] = useState(draft.users[0]?.id ?? '');
  const [identityLines, setIdentityLines] = useState('');
  const [identityStatus, setIdentityStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [identityMessage, setIdentityMessage] = useState('');

  useEffect(() => {
    if (!draftDomain) {
      return;
    }
    setAddressLines(formatRouteLines(draftDomain.addresses));
    setAliasLines(formatRouteLines(draftDomain.aliases));
    setCatchAllMailboxes(catchAllMailboxIds(draftDomain));
    setLoginPolicy(draftDomain.identityPolicy.login);
    setSenderPolicy(draftDomain.identityPolicy.sender);
  }, [domain, draft, draftDomain]);

  useEffect(() => {
    const user = draft.users.some((candidate) => candidate.id === identityUser) ? identityUser : (draft.users[0]?.id ?? '');
    if (user !== identityUser) {
      setIdentityUser(user);
    }
    setIdentityLines((draft.sendIdentities[user] ?? []).join('\n'));
  }, [draft, identityUser]);

  async function saveRoutes(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setRouteStatus('saving');
    setRouteMessage('Validating the complete domain route candidate...');
    try {
      const next = await replaceMailRouteDraftDomain({
        domain,
        addresses: parseRouteLines(addressLines),
        aliases: parseRouteLines(aliasLines),
        catchAllMailboxes,
        loginPolicy,
        senderPolicy,
        expectedRevision: draft.revision
      });
      onDraftChange(next);
      setRouteStatus('success');
      setRouteMessage(`Domain policy is in inactive draft revision ${next.revision}.`);
    } catch (error: unknown) {
      setRouteStatus('error');
      setRouteMessage(error instanceof Error && !(error instanceof ApiError) ? error.message : routeDraftErrorMessage(error));
    }
  }

  async function saveIdentities(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIdentityStatus('saving');
    setIdentityMessage('Validating sender ownership against the complete route graph...');
    try {
      const identities = Array.from(new Set(identityLines.split('\n').map((line) => line.trim().toLowerCase()).filter(Boolean)));
      const next = await replaceMailRouteDraftSendIdentities({
        userId: identityUser,
        identities,
        expectedRevision: draft.revision
      });
      onDraftChange(next);
      setIdentityStatus('success');
      setIdentityMessage(`Send identities are in inactive draft revision ${next.revision}.`);
    } catch (error: unknown) {
      setIdentityStatus('error');
      setIdentityMessage(routeDraftErrorMessage(error));
    }
  }

  if (!draftDomain) {
    return <section className="route-draft-editor"><p className="route-draft-feedback error">{domain} is absent from the current draft. Domain creation and provider onboarding remain a separate planned operation.</p></section>;
  }

  const busy = routeStatus === 'saving' || identityStatus === 'saving';
  const routeMailboxIdSet = new Set(routeMailboxIds(draftDomain));
  const selectedDomain = domain.toLowerCase();
  const sameDomainMailboxes = draft.mailboxes.filter((mailbox) => {
    const primaryEmail = mailbox.primaryEmail?.trim().toLowerCase();
    if (primaryEmail) {
      return primaryEmail.slice(primaryEmail.lastIndexOf('@') + 1) === selectedDomain;
    }
    return routeMailboxIdSet.has(mailbox.id);
  });
  const sameDomainMailboxIds = new Set(sameDomainMailboxes.map((mailbox) => mailbox.id));
  const unresolvedCatchAllCount = catchAllMailboxes.filter((mailboxId) => !sameDomainMailboxIds.has(mailboxId)).length;

  function mailboxUser(mailbox: MailAdminMailbox) {
    return draft.users.find((user) => user.primaryMailbox === mailbox.id);
  }

  function toggleCatchAllMailbox(mailboxId: string, checked: boolean) {
    setCatchAllMailboxes((current) => checked
      ? Array.from(new Set([...current, mailboxId]))
      : current.filter((candidate) => candidate !== mailboxId));
  }

  return (
    <section className="route-draft-editor" aria-label={`Edit inactive route draft for ${domain}`}>
      <div className="route-draft-editor-heading">
        <div>
          <p className="section-label">Inactive draft revision {draft.revision}</p>
          <h4>Edit catch-all destinations and send identities</h4>
          <p>Choose one or more mailboxes in this domain for fallback delivery. Exact routes and aliases remain visible here for review; edit mailbox aliases from the Mailboxes section.</p>
          <p>One save replaces the complete domain candidate. Primary email is the only login address; aliases and catch-all destinations receive mail only.</p>
        </div>
        <span>Not active</span>
      </div>
      <div className="route-draft-grid">
        <form onSubmit={saveRoutes}>
          <div className="route-draft-route-summary">
            <div className="route-draft-section-heading"><strong>Current direct routes</strong><small>Exact routes and aliases are read-only in this domain view.</small></div>
            {draftDomain.addresses.map((route) => <div className="route-draft-route" key={`exact-${route.address}`}><strong>{route.address}</strong><small>Exact · {mailboxDestinationLabel(route.mailbox, draft.mailboxes)}</small></div>)}
            {draftDomain.aliases.map((route) => <div className="route-draft-route" key={`alias-${route.address}`}><strong>{route.address}</strong><small>Alias · {mailboxDestinationLabel(route.mailbox, draft.mailboxes)}</small></div>)}
            {draftDomain.addresses.length + draftDomain.aliases.length === 0 ? <p className="route-draft-help">No exact or alias routes are configured for this domain.</p> : null}
          </div>
          <fieldset className="catch-all-fieldset">
            <legend>Catch-all destinations</legend>
            <p className="route-draft-help">Select one or more same-domain mailboxes. Catch-all delivery is receive-only and never creates a login.</p>
            {sameDomainMailboxes.length > 0 ? <div className="catch-all-options">{sameDomainMailboxes.map((mailbox, index) => <label className="catch-all-option" key={mailbox.id}><input checked={catchAllMailboxes.includes(mailbox.id)} disabled={busy} onChange={(event) => toggleCatchAllMailbox(mailbox.id, event.currentTarget.checked)} type="checkbox" /><span>{mailboxOptionLabel(mailbox, mailboxUser(mailbox), index)}</span></label>)}</div> : <p className="route-draft-help">No same-domain mailbox has a known primary email or route yet.</p>}
            {unresolvedCatchAllCount > 0 ? <p className="route-draft-feedback warning" role="status">{unresolvedCatchAllCount} saved destination{unresolvedCatchAllCount === 1 ? '' : 's'} is outside the current mailbox page and will be cleared if you save this draft.</p> : null}
            {catchAllMailboxes.length > 0 ? <button disabled={busy} onClick={() => setCatchAllMailboxes([])} type="button">Clear catch-all destinations</button> : null}
          </fieldset>
          <p className="route-draft-help">The stored login policy is retained for historical snapshots; it does not make aliases or catch-all addresses valid login names.</p>
          <button disabled={busy} type="submit">{routeStatus === 'saving' ? 'Validating routes...' : 'Save domain draft'}</button>
          {routeMessage ? <p className={`route-draft-feedback ${routeStatus}`} role={routeStatus === 'error' ? 'alert' : 'status'}>{routeMessage}</p> : null}
        </form>
        <form onSubmit={saveIdentities}>
          <label><span>Route user</span><select disabled={busy} onChange={(event) => setIdentityUser(event.currentTarget.value)} value={identityUser}>{draft.users.map((user, index) => <option key={user.id} value={user.id}>{user.displayName || `Route user ${index + 1}`}</option>)}</select></label>
          <label><span>Send identities — one address per line</span><textarea disabled={busy} onChange={(event) => setIdentityLines(event.currentTarget.value)} rows={10} spellCheck={false} value={identityLines} /></label>
          <p className="route-draft-help">{catchAllMailboxes.length > 1 ? 'Multiple catch-all destinations are delivery-only; this fanout does not grant send-as rights. Use curated Send identities for outbound mail.' : 'A single catch-all destination may use arbitrary addresses in a routed-sender domain, except exact addresses or aliases owned by another mailbox.'}</p>
          <button disabled={busy || !identityUser} type="submit">{identityStatus === 'saving' ? 'Validating identities...' : 'Save identity draft'}</button>
          {identityMessage ? <p className={`route-draft-feedback ${identityStatus}`} role={identityStatus === 'error' ? 'alert' : 'status'}>{identityMessage}</p> : null}
        </form>
      </div>
    </section>
  );
}

function formatRouteLines(routes: MailAdminAddressRoute[]): string {
  return routes.map((route) => `${route.address}=${route.mailbox}`).join('\n');
}

function parseRouteLines(value: string): MailAdminAddressRoute[] {
  const seen = new Set<string>();
  return value.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
    const separator = line.indexOf('=');
    if (separator <= 0 || separator === line.length - 1) {
      throw new Error(`Invalid route line: ${line}. Use address=mailbox.`);
    }
    const address = line.slice(0, separator).trim().toLowerCase();
    const mailbox = line.slice(separator + 1).trim();
    if (seen.has(address)) {
      throw new Error(`Duplicate route address: ${address}.`);
    }
    seen.add(address);
    return { address, mailbox };
  });
}

function routeDraftErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return 'The draft changed in another session. Reload the latest revision before retrying.';
  }
  if (error instanceof ApiError && error.status === 400) {
    return 'The candidate was rejected. Check mailbox ownership, route addresses, and sender identity scope.';
  }
  return 'The draft was not changed. Reload the latest revision and check server availability.';
}

function routeApplicationErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return 'The draft changed before it could be applied. Reload the latest revision and review it again.';
  }
  if (error instanceof ApiError && error.status === 403) {
    return 'Your session may have expired or admin access is unavailable. Reload the page and sign in again to check apply status.';
  }
  if (error instanceof ApiError && error.status === 503) {
    return 'The mail services are restarting. Reconnect to confirm apply progress.';
  }
  return 'The server could not complete the apply request. Reload apply status before trying again.';
}

function LlmBudgetPanel({
  error,
  onSave,
  snapshot,
  status
}: {
  error: string;
  onSave: (settings: LlmBudgetSettings) => Promise<void>;
  snapshot: LlmBudgetSnapshot | null;
  status: 'loading' | 'ready' | 'saving' | 'error';
}) {
  const [monthlyLimit, setMonthlyLimit] = useState('10.00');
  const [inputPrice, setInputPrice] = useState('1.00');
  const [outputPrice, setOutputPrice] = useState('10.00');
  const [maxInputTokens, setMaxInputTokens] = useState('65536');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!snapshot) {
      return;
    }
    setMonthlyLimit(microUsdToDollars(snapshot.settings.monthlyLimitMicroUsd));
    setInputPrice(microUsdToDollars(snapshot.settings.inputPriceMicroUsdPerMillion));
    setOutputPrice(microUsdToDollars(snapshot.settings.outputPriceMicroUsdPerMillion));
    setMaxInputTokens(snapshot.settings.maxInputTokensPerRequest.toString());
  }, [snapshot]);

  const committed = snapshot ? snapshot.actualMicroUsd + snapshot.reservedMicroUsd : 0;
  const progress = snapshot && snapshot.settings.monthlyLimitMicroUsd > 0
    ? Math.min(100, committed / snapshot.settings.monthlyLimitMicroUsd * 100)
    : committed > 0 ? 100 : 0;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!snapshot) {
      return;
    }

    const monthlyLimitMicroUsd = dollarsToMicroUsd(monthlyLimit);
    const inputPriceMicroUsdPerMillion = dollarsToMicroUsd(inputPrice);
    const outputPriceMicroUsdPerMillion = dollarsToMicroUsd(outputPrice);
    const maxInputTokensPerRequest = Number.parseInt(maxInputTokens, 10);
    if (monthlyLimitMicroUsd === null || inputPriceMicroUsdPerMillion === null || inputPriceMicroUsdPerMillion <= 0 || outputPriceMicroUsdPerMillion === null || outputPriceMicroUsdPerMillion <= 0 || !Number.isSafeInteger(maxInputTokensPerRequest) || maxInputTokensPerRequest <= 0) {
      setFormError('Use a non-negative monthly ceiling, positive token prices, and a positive whole-token request ceiling.');
      return;
    }

    setFormError('');
    void onSave({
      monthlyLimitMicroUsd,
      inputPriceMicroUsdPerMillion,
      outputPriceMicroUsdPerMillion,
      maxInputTokensPerRequest,
      version: snapshot.settings.version
    });
  }

  return (
    <section className="llm-budget-panel" aria-label="LLM classifier budget">
      <div className="llm-budget-summary">
        <div><p className="section-label">Classifier guardrail / {snapshot?.period ?? 'current UTC month'}</p><h3>LLM spend has a hard ceiling.</h3><p>Capacity is reserved before a model request. At the limit, or if budget storage is unavailable, mail continues through the built-in classifier without an LLM call.</p></div>
        <div className="llm-budget-meter" aria-label={snapshot ? `${progress.toFixed(1)} percent of budget committed` : 'Budget status unavailable'}>
          <span style={{ width: `${progress}%` }} />
        </div>
        <dl className="llm-budget-stats">
          <div><dt>Spent</dt><dd>{snapshot ? formatUsd(snapshot.actualMicroUsd) : 'Unavailable'}</dd></div>
          <div><dt>Reserved</dt><dd>{snapshot ? formatUsd(snapshot.reservedMicroUsd) : 'Unavailable'}</dd></div>
          <div><dt>Remaining</dt><dd>{snapshot ? formatUsd(snapshot.remainingMicroUsd) : 'Offline only'}</dd></div>
          <div><dt>Calls / skipped</dt><dd>{snapshot ? `${snapshot.modelCalls} / ${snapshot.skippedCalls}` : '0 / all'}</dd></div>
        </dl>
      </div>
      <form className="llm-budget-form" onSubmit={submit}>
        <label><span>Monthly ceiling, USD</span><input disabled={!snapshot || status === 'saving'} min="0" onChange={(event) => setMonthlyLimit(event.currentTarget.value)} step="0.01" type="number" value={monthlyLimit} /></label>
        <label><span>Input price, USD / 1M</span><input disabled={!snapshot || status === 'saving'} min="0.000001" onChange={(event) => setInputPrice(event.currentTarget.value)} step="0.000001" type="number" value={inputPrice} /></label>
        <label><span>Output price, USD / 1M</span><input disabled={!snapshot || status === 'saving'} min="0.000001" onChange={(event) => setOutputPrice(event.currentTarget.value)} step="0.000001" type="number" value={outputPrice} /></label>
        <label><span>Input tokens / request</span><input disabled={!snapshot || status === 'saving'} min="1" onChange={(event) => setMaxInputTokens(event.currentTarget.value)} step="1" type="number" value={maxInputTokens} /></label>
        <button disabled={!snapshot || status === 'saving'} type="submit">{status === 'saving' ? 'Saving guardrail...' : 'Save budget'}</button>
        <small>Price fields are explicit because provider pricing is not treated as a timeless constant.</small>
        {status === 'loading' ? <p className="llm-budget-notice">Loading the authoritative monthly ledger...</p> : null}
        {formError || error ? <p className="llm-budget-error" role="alert">{formError || error}</p> : null}
      </form>
    </section>
  );
}

function dollarsToMicroUsd(value: string): number | null {
  const dollars = Number(value);
  if (!Number.isFinite(dollars) || dollars < 0) {
    return null;
  }
  const microUsd = Math.round(dollars * 1_000_000);
  return Number.isSafeInteger(microUsd) ? microUsd : null;
}

function microUsdToDollars(value: number): string {
  return (value / 1_000_000).toFixed(6).replace(/0+$/, '').replace(/\.$/, '.00');
}

function formatUsd(microUsd: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }).format(microUsd / 1_000_000);
}

function AdminSectionButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return <button className={active ? 'domain-subnav-button active' : 'domain-subnav-button'} onClick={onClick} type="button">{label}</button>;
}

function HoneypotPanel() {
  const [honeypots, setHoneypots] = useState<MailAdminHoneypot[]>([]);
  const [listStatus, setListStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [listError, setListError] = useState('');
  const [address, setAddress] = useState('');
  const [mutationStatus, setMutationStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [mutationMessage, setMutationMessage] = useState('');

  async function reloadHoneypots() {
    setListStatus('loading');
    setListError('');
    try {
      const nextHoneypots = await loadMailAdminHoneypots();
      setHoneypots(sortHoneypots(nextHoneypots));
      setListStatus('ready');
      setMutationStatus('idle');
      setMutationMessage('');
    } catch {
      setListStatus('error');
      setListError('The honeypot list could not be loaded. Reload the list and check admin access before retrying.');
    }
  }

  useEffect(() => {
    void reloadHoneypots();
  }, []);

  function submitHoneypot(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedAddress = address.trim().toLowerCase();
    if (!normalizedAddress) {
      setMutationStatus('error');
      setMutationMessage('Enter a honeypot address before adding it.');
      return;
    }

    setMutationStatus('saving');
    setMutationMessage('Adding honeypot address...');
    createMailAdminHoneypot(normalizedAddress)
      .then((created) => {
        setHoneypots((current) => sortHoneypots([...current.filter((candidate) => candidate.id !== created.id), created]));
        setAddress('');
        setMutationStatus('success');
        setMutationMessage(`${created.address} was added. Classifier caches refresh within 30 seconds; global visibility does not create a global match scope.`);
      })
      .catch(() => {
        setMutationStatus('error');
        setMutationMessage('The address was not added. The current list is unchanged; reload the list and retry.');
      });
  }

  function removeHoneypot(honeypot: MailAdminHoneypot) {
    if (!window.confirm(`Remove ${honeypot.address} from the global honeypot list?`)) {
      return;
    }

    setMutationStatus('saving');
    setMutationMessage(`Removing ${honeypot.address}...`);
    deleteMailAdminHoneypot(honeypot)
      .then(() => {
        setHoneypots((current) => current.filter((candidate) => candidate.id !== honeypot.id));
        setMutationStatus('success');
        setMutationMessage(`${honeypot.address} was removed. Classifier caches refresh within 30 seconds.`);
      })
      .catch(() => {
        setMutationStatus('error');
        setMutationMessage('The address was not removed. The current list is unchanged; reload the list and retry.');
      });
  }

  return (
    <section className="honeypot-panel" aria-label="Global honeypot list">
      <div className="honeypot-intro">
        <div>
          <p className="section-label">Global trap recipients</p>
          <h4>Manage the addresses that seed exact-content protection.</h4>
        </div>
        <p>Every message delivered exclusively to addresses below in one destination scope is classified as spam and teaches an exact-content fingerprint. Global visibility does not create a global match scope.</p>
      </div>

      <form className="honeypot-form" onSubmit={submitHoneypot}>
        <label htmlFor="honeypot-address"><span>Address</span><input autoCapitalize="none" id="honeypot-address" inputMode="email" onChange={(event) => setAddress(event.currentTarget.value)} placeholder="trap@example.com" spellCheck={false} type="text" value={address} /></label>
        <button className="admin-primary-action" disabled={mutationStatus === 'saving'} type="submit">{mutationStatus === 'saving' ? 'Saving...' : 'Add address'}</button>
      </form>

      {listError ? <div className="honeypot-feedback error" role="alert"><span>{listError}</span><button onClick={() => void reloadHoneypots()} type="button">Reload list</button></div> : null}
      {mutationMessage ? <div className={`honeypot-feedback ${mutationStatus}`} role={mutationStatus === 'error' ? 'alert' : 'status'}><span>{mutationMessage}</span>{mutationStatus === 'error' ? <button onClick={() => void reloadHoneypots()} type="button">Reload list</button> : null}</div> : null}

      {listStatus === 'loading' && honeypots.length === 0 ? <p className="honeypot-empty" aria-live="polite">Loading configured honeypot addresses...</p> : null}
      {listStatus === 'ready' && honeypots.length === 0 ? <p className="honeypot-empty">No honeypot addresses are configured.</p> : null}
      {honeypots.length > 0 ? (
        <div className="honeypot-table-frame">
          <table className="honeypot-table">
            <caption className="sr-only">Global honeypot addresses</caption>
            <thead><tr><th scope="col">Address</th><th scope="col">Destination domain</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{honeypots.map((honeypot) => <tr key={honeypot.id}><td><strong>{honeypot.address}</strong></td><td>{honeypot.destinationDomain}</td><td><button className="honeypot-remove" disabled={mutationStatus === 'saving'} onClick={() => removeHoneypot(honeypot)} type="button">Remove</button></td></tr>)}</tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function sortHoneypots(honeypots: MailAdminHoneypot[]): MailAdminHoneypot[] {
  return [...honeypots].sort((left, right) => left.destinationDomain.localeCompare(right.destinationDomain) || left.address.localeCompare(right.address));
}

function adminSectionTitle(section: AdminSection): string {
  switch (section) {
    case 'overview': return 'Domain overview';
    case 'mailboxes': return 'Mailboxes';
    case 'routing': return 'Address routing';
    case 'filing': return 'Filing rules';
    case 'dns': return 'Delivery & DNS';
    case 'honeypots': return 'Honeypot addresses';
  }
}

function EmptyAdminState({ onOpenWizard, title = 'No domain selected' }: { onOpenWizard?: (trigger: HTMLButtonElement) => void; title?: string }) {
  return <section className="admin-empty-state"><span className="empty-state-mark" aria-hidden="true">+</span><h4>{title}</h4><p>Domains are added through a guarded setup preview. Nothing is written to AWS, DNS, SES, or the route store from this screen.</p>{onOpenWizard ? <button className="admin-primary-action" onClick={(event) => onOpenWizard(event.currentTarget)} type="button">+ New domain</button> : null}</section>;
}

function AdminDomainOverview({ domain, mailboxCount, mailboxes, routeCount, warnings }: { domain: MailAdminDomainDetail; mailboxCount: number; mailboxes: MailAdminMailbox[]; routeCount: number; warnings?: string[] }) {
  const domainWarnings = warnings?.filter((warning) => warning.toLowerCase().includes(domain.domain.toLowerCase())) ?? [];
  const warningFeedAvailable = warnings !== undefined;
  const catchAll = catchAllMailboxIds(domain);
  const destinations = mailboxDestinationSummary(catchAll, mailboxes);
  return <div className="admin-overview-view"><div className="overview-stat-strip"><div><span>Mailboxes</span><strong>{mailboxCount}</strong><small>routed to this domain</small></div><div><span>Routes</span><strong>{routeCount}</strong><small>{domain.addresses.length} exact · {domain.aliases.length} aliases</small></div><div><span>Catch-all</span><strong>{catchAll.length > 0 ? 'On' : 'Off'}</strong><small>{catchAll.length > 0 ? `delivers to ${destinations}` : 'Requires a review'}</small></div></div><div className="overview-columns"><section className="admin-rule-sheet"><div className="admin-section-intro"><p className="section-label">Routing order</p><h4>Exact first, aliases second.</h4><p>Inbound mail is evaluated against this domain policy before the catch-all fallback.</p></div><ol className="routing-order-list"><li><span>01</span><div><strong>Exact addresses</strong><small>{domain.addresses.length} configured route{domain.addresses.length === 1 ? '' : 's'}</small></div></li><li><span>02</span><div><strong>Aliases</strong><small>{domain.aliases.length} configured alias{domain.aliases.length === 1 ? '' : 'es'}</small></div></li><li><span>03</span><div><strong>Catch-all</strong><small>{catchAll.length > 0 ? `Fallback to ${destinations}` : 'Not configured'}</small></div></li></ol></section><section className="admin-rule-sheet warning-sheet"><div className="admin-section-intro"><p className="section-label">Attention</p><h4>{warningFeedAvailable ? (domainWarnings.length > 0 ? `${domainWarnings.length} policy gap${domainWarnings.length === 1 ? '' : 's'}` : 'No local warnings') : 'No warning feed in bounded view'}</h4><p>{warningFeedAvailable ? 'Warnings come from the current route snapshot and are not provider health checks.' : 'This bounded endpoint does not include provider health or warning aggregation.'}</p></div><div className="warning-list">{warningFeedAvailable ? (domainWarnings.length > 0 ? domainWarnings.map((warning) => <span key={warning}>{warning}</span>) : <span className="warning-clear">Route policy has no recorded gaps for this domain.</span>) : <span className="warning-clear">Warning aggregation is not returned by the selected-domain endpoint.</span>}</div></section></div></div>;
}

function AdminRoutingView({ domain, mailboxes }: { domain: MailAdminDomainDetail; mailboxes: MailAdminMailbox[] }) {
  const routes = [...domain.addresses.map((route) => ({ ...route, kind: 'Exact' })), ...domain.aliases.map((route) => ({ ...route, kind: 'Alias' }))];
  const catchAll = catchAllMailboxIds(domain);
  return <div className="admin-routing-view"><div className="admin-section-intro"><p className="section-label">Active read-only policy</p><h4>Address routing for {domain.domain}</h4><p>These routes are sourced from the active mail router. If draft editing is enabled, the separate editor below never changes this active view implicitly.</p></div><div className="routing-table-frame"><table className="routing-table"><thead><tr><th scope="col">Address</th><th scope="col">Type</th><th scope="col">Destination</th></tr></thead><tbody>{routes.map((route) => <tr key={`${route.kind}-${route.address}`}><td><strong>{route.address}</strong></td><td><span className="route-type">{route.kind}</span></td><td>{mailboxDestinationLabel(route.mailbox, mailboxes)}</td></tr>)}<tr className="catch-all-row"><td><strong>*@{domain.domain}</strong></td><td><span className={catchAll.length > 0 ? 'route-type active' : 'route-type warning'}>Catch-all</span></td><td>{mailboxDestinationSummary(catchAll, mailboxes)}</td></tr></tbody></table>{routes.length === 0 && catchAll.length === 0 ? <p className="table-empty">No routes are configured for this domain.</p> : null}</div><p className="read-only-note"><span aria-hidden="true">i</span> Applying a saved draft is a separate action. Mail delivery keeps using the current revision until you apply it.</p></div>;
}

function AdminDeliveryDnsView({ domain, warnings }: { domain: MailAdminDomainDetail; warnings?: string[] }) {
  const domainWarnings = warnings?.filter((warning) => warning.toLowerCase().includes(domain.domain.toLowerCase())) ?? [];
  return <div className="admin-dns-view"><div className="admin-section-intro"><p className="section-label">Provider handoff</p><h4>Delivery &amp; DNS for {domain.domain}</h4><p>Use this checklist to prepare a provider change. It intentionally reports no live AWS, SES, or DNS state.</p></div><div className="provider-checklist"><div className="provider-row"><span className="provider-icon">MX</span><div><strong>Inbound delivery</strong><small>SES receipt path and mailbox worker handoff</small></div><span className="provider-state pending">Preflight required</span></div><div className="provider-row"><span className="provider-icon">DK</span><div><strong>Authentication</strong><small>SPF, DKIM, and DMARC records for {domain.domain}</small></div><span className="provider-state pending">Not checked</span></div><div className="provider-row"><span className="provider-icon">OUT</span><div><strong>Outbound delivery</strong><small>SES identity and sending authorization</small></div><span className="provider-state pending">Not checked</span></div></div><div className="dns-guardrail"><strong>Safe boundary</strong><p>No provider mutation is available here. The New domain flow creates a setup preview only; apply DNS and AWS changes through the approved deployment path.</p>{domainWarnings.length > 0 ? <small>{domainWarnings.length} route warning{domainWarnings.length === 1 ? '' : 's'} still need review.</small> : null}</div></div>;
}

function AdminMailboxInspector({ row }: { row: AdminMailboxRow }) {
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const sendIdentities = row.account?.sendIdentities ?? [];
  const displayName = mailboxDisplayName(row.mailbox, row.user);
  const primaryEmail = mailboxPrimaryEmail(row.mailbox, row.user);
  return <aside className="mailbox-inspector" aria-label="Mailbox inspector"><div className="inspector-heading"><div><p className="section-label">Selected mailbox</p><h4>{displayName}</h4><small>{primaryEmail ?? 'Primary email unavailable'}</small></div><span className={`mailbox-status ${row.status}`}>{row.status === 'pending' ? 'Pending apply' : row.status === 'ready' ? 'Ready' : 'Needs attention'}</span></div><dl className="inspector-details"><div><dt>Owner</dt><dd>{row.user?.displayName ?? 'Owner unavailable'}</dd></div><div><dt>Primary</dt><dd>{primaryEmail ?? 'No login email configured'}</dd></div><div><dt>Folders</dt><dd>{Object.values(row.mailbox.folders).join(' · ') || 'None reported'}</dd></div></dl><div className="inspector-route-block"><span className="inspector-label">Routes</span>{row.addresses.length + row.aliases.length > 0 ? <>{row.addresses.map((route) => <span className="inspector-route" key={route.address}><b>Exact</b>{route.address}</span>)}{row.aliases.map((route) => <span className="inspector-route" key={route.address}><b>Alias</b>{route.address}</span>)}</> : <span className="inspector-muted">No exact or alias routes</span>}</div><div className="inspector-route-block"><span className="inspector-label">Send identities</span>{sendIdentities.length > 0 ? sendIdentities.map((address) => <span className="inspector-route" key={address}>{address}</span>) : <span className="inspector-muted">No send identities</span>}</div>{row.account ? <div className="inspector-action-block"><div><strong>Account action</strong><small>{identityStatusDetail(row.account)}</small></div><button className="inspector-action-button" onClick={() => setPasswordResetOpen((open) => !open)} type="button">{passwordResetOpen ? 'Close password reset' : 'Reset password'}</button>{passwordResetOpen ? <PasswordResetPanel account={row.account} /> : null}</div> : <p className="inspector-muted">Identity account state is unavailable, so password actions are disabled.</p>}</aside>;
}

function NewDomainWizard({ domains, onCheckDomain, onClose, returnFocusRef }: {
  domains: string[];
  onCheckDomain?: (domain: string) => Promise<'duplicate' | 'available' | 'unavailable'>;
  onClose: () => void;
  returnFocusRef?: RefObject<HTMLButtonElement | null>;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [domain, setDomain] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [sesAccountId, setSesAccountId] = useState('');
  const [route53AccountId, setRoute53AccountId] = useState('');
  const [hostedZoneId, setHostedZoneId] = useState('');
  const [automateDns, setAutomateDns] = useState(false);
  const [receiptRuleSet, setReceiptRuleSet] = useState('mailer-inbound');
  const [runtimeRoleArn, setRuntimeRoleArn] = useState('');
  const [mailFromSubdomain, setMailFromSubdomain] = useState('bounce');
  const [providerPlan, setProviderPlan] = useState<DomainOnboardingPlan | null>(null);
  const [wizardError, setWizardError] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const dialogRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) {
        return;
      }

      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])'))
        .filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true');
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      returnFocusRef?.current?.focus();
    };
  }, []);

  async function validateDomain(): Promise<string | null> {
    const normalized = domain.trim().toLowerCase();
    if (!isValidDomainName(normalized)) {
      return 'Enter a fully qualified domain, such as example.com.';
    }
    if (domains.some((existing) => existing.toLowerCase() === normalized)) {
      return `${normalized} is already configured.`;
    }
    if (onCheckDomain) {
      const availability = await onCheckDomain(normalized);
      if (availability === 'duplicate') {
        return `${normalized} is already configured.`;
      }
      if (availability === 'unavailable') {
        return 'The current domain index could not verify duplicates. Try again before generating a preview.';
      }
    }
    return null;
  }

  async function goToDelivery() {
    setIsChecking(true);
    const error = await validateDomain();
    setIsChecking(false);
    if (error) {
      setWizardError(error);
      return;
    }
    setDomain(domain.trim().toLowerCase());
    setWizardError('');
    setStep(2);
  }

  async function goToPreview() {
    setIsChecking(true);
    const error = await validateDomain();
    if (error) {
      setIsChecking(false);
      setWizardError(error);
      setStep(1);
      return;
    }
    if (!/^\d{12}$/.test(sesAccountId.trim())) {
      setIsChecking(false);
      setWizardError('Enter the 12-digit AWS account that owns the SES identity.');
      return;
    }
    if (!runtimeRoleArn.trim()) {
      setIsChecking(false);
      setWizardError('Enter the runtime IAM role that will receive the domain-scoped ses:SendRawEmail grant.');
      return;
    }
    if (automateDns && (!/^\d{12}$/.test(route53AccountId.trim()) || !hostedZoneId.trim())) {
      setIsChecking(false);
      setWizardError('Automated DNS requires the Route53 account and hosted-zone ID.');
      return;
    }

    try {
      const plan = await planMailAdminDomain({
        domain: domain.trim().toLowerCase(),
        region: region.trim(),
        sesAccountId: sesAccountId.trim(),
        route53AccountId: route53AccountId.trim() || undefined,
        hostedZoneId: hostedZoneId.trim() || undefined,
        automateDns,
        receiptRuleSet: receiptRuleSet.trim(),
        runtimeRoleArn: runtimeRoleArn.trim(),
        mailFromSubdomain: mailFromSubdomain.trim()
      });
      setProviderPlan(plan);
      setWizardError('');
      setStep(3);
    } catch {
      setProviderPlan(null);
      setWizardError('The server rejected this provider plan. Recheck the AWS account, role, region, and DNS coordinates.');
    } finally {
      setIsChecking(false);
    }
  }

  return (
    <div className="wizard-backdrop" role="presentation">
      <section
        aria-label="New domain setup preview"
        aria-labelledby="new-domain-wizard-title"
        aria-modal="true"
        className="domain-wizard"
        ref={dialogRef}
        role="dialog"
      >
        <header className="wizard-header">
          <div>
            <p className="section-label">Guarded setup</p>
            <h3 id="new-domain-wizard-title">{step === 3 ? 'Setup preview' : 'Add a domain'}</h3>
          </div>
          <button aria-label="Close new domain wizard" className="wizard-close" onClick={onClose} ref={closeButtonRef} type="button">x</button>
        </header>
        <div className="wizard-steps" aria-label="Wizard progress"><span className={step >= 1 ? 'current' : ''}>01 Domain</span><span className={step >= 2 ? 'current' : ''}>02 Delivery</span><span className={step >= 3 ? 'current' : ''}>03 Preview</span></div>
        {step === 1 ? <div className="wizard-body">
          <label className="wizard-field"><span>Domain name</span><input aria-describedby={wizardError ? 'domain-wizard-help domain-wizard-error' : 'domain-wizard-help'} aria-invalid={wizardError ? 'true' : 'false'} onChange={(event) => { setDomain(event.currentTarget.value.toLowerCase()); setWizardError(''); }} placeholder="example.com" type="text" value={domain} /></label>
          <p className="wizard-help" id="domain-wizard-help">Use the domain you control. This wizard only prepares a reviewable setup brief.</p>
          {wizardError ? <p className="wizard-error" id="domain-wizard-error" role="alert">{wizardError}</p> : null}
          <div className="wizard-actions"><button className="wizard-secondary" onClick={onClose} type="button">Cancel</button><button className="admin-primary-action" disabled={domain.trim().length === 0 || isChecking} onClick={goToDelivery} type="button">{isChecking ? 'Checking...' : 'Continue'}</button></div>
        </div> : null}
        {step === 2 ? <div className="wizard-body">
          <div className="wizard-domain-preview"><span>Domain</span><strong>{domain}</strong></div>
          <label className="wizard-field"><span>AWS region</span><input onChange={(event) => setRegion(event.currentTarget.value)} placeholder="us-east-1" type="text" value={region} /></label>
          <label className="wizard-field"><span>SES AWS account ID</span><input inputMode="numeric" onChange={(event) => setSesAccountId(event.currentTarget.value)} placeholder="123456789012" type="text" value={sesAccountId} /></label>
          <label className="wizard-field"><span>Runtime IAM role ARN</span><input onChange={(event) => setRuntimeRoleArn(event.currentTarget.value)} placeholder="arn:aws:iam::123456789012:role/mailer-smtp" type="text" value={runtimeRoleArn} /></label>
          <label className="wizard-field"><span>Receipt rule set</span><input onChange={(event) => setReceiptRuleSet(event.currentTarget.value)} type="text" value={receiptRuleSet} /></label>
          <label className="wizard-field"><span>Custom MAIL FROM label</span><input onChange={(event) => setMailFromSubdomain(event.currentTarget.value)} type="text" value={mailFromSubdomain} /></label>
          <label className="wizard-field"><span>DNS publication</span><select onChange={(event) => setAutomateDns(event.currentTarget.value === 'route53')} value={automateDns ? 'route53' : 'manual'}><option value="manual">Manual or external provider</option><option value="route53">Automate through an authorized Route53 account</option></select></label>
          <label className="wizard-field"><span>Route53 AWS account ID</span><input inputMode="numeric" onChange={(event) => setRoute53AccountId(event.currentTarget.value)} placeholder="Optional for manual DNS" type="text" value={route53AccountId} /></label>
          <label className="wizard-field"><span>Route53 hosted-zone ID</span><input onChange={(event) => setHostedZoneId(event.currentTarget.value)} placeholder="Optional for manual DNS" type="text" value={hostedZoneId} /></label>
          <p className="wizard-help">After the domain plan, choose one or more same-domain catch-all destinations in the editable route draft.</p>
          {wizardError ? <p className="wizard-error" role="alert">{wizardError}</p> : null}
          <div className="wizard-safety-callout"><strong>Plan only</strong><span>The server computes SES, DKIM, MAIL FROM, inbound MX, receipt-rule, and IAM steps. It does not call AWS.</span></div>
          <div className="wizard-actions"><button className="wizard-secondary" onClick={() => setStep(1)} type="button">Back</button><button className="admin-primary-action" disabled={isChecking} onClick={goToPreview} type="button">{isChecking ? 'Checking...' : 'Generate setup preview'}</button></div>
        </div> : null}
        {step === 3 ? <div className="wizard-body">
          <div className="preview-banner"><span className="preview-banner-mark">!</span><div><strong>Preview only</strong><p>This is a proposed setup for review. No live AWS or DNS mutation has occurred.</p></div></div>
          <dl className="setup-preview-list"><div><dt>Domain</dt><dd>{providerPlan?.domain ?? domain}</dd></div><div><dt>Catch-all</dt><dd>Choose destinations in the route editor</dd></div><div><dt>SES</dt><dd>{providerPlan ? `${providerPlan.sesAccountId} / ${providerPlan.region}` : 'Unavailable'}</dd></div><div><dt>DNS</dt><dd>{providerPlan?.steps.some((step) => step.kind.startsWith('dns.') && step.manual) ? 'Manual or external publication' : 'Requested Route53 automation (unverified)'}</dd></div></dl>
          <div className="wizard-checklist">{providerPlan?.steps.map((step) => <span key={step.id}>{step.manual ? 'Manual' : 'Planned'} · {step.kind} · {step.target}</span>)}</div>
          {providerPlan?.warnings.map((warning) => <p className="wizard-error" key={warning}>{warning}</p>)}
          <p className="wizard-help">Activation remains blocked on: {providerPlan?.activationBlockers.join(', ') ?? 'provider plan unavailable'}.</p>
          <div className="wizard-actions"><button className="wizard-secondary" onClick={() => setStep(2)} type="button">Back</button><button className="admin-primary-action" onClick={onClose} type="button">Close preview</button></div>
        </div> : null}
      </section>
    </div>
  );
}

function isValidDomainName(value: string): boolean {
  return /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i.test(value.trim());
}

const folderRuleOperators: Record<string, string[]> = {
  classification: ['equals'],
  confidence: ['at_least'],
  recipient: ['equals', 'contains', 'ends_with'],
  from: ['equals', 'contains', 'ends_with'],
  subject: ['equals', 'contains', 'ends_with'],
  spam_signal: ['equals', 'contains', 'present'],
  suspicious_signal: ['equals', 'contains', 'present'],
  safe_signal: ['equals', 'contains', 'present'],
  prompt_injection_signal: ['equals', 'contains', 'present']
};

function FolderRulePanel({
  mailboxes,
  onRulesChange,
  rules,
  scope = 'admin'
}: {
  mailboxes: MailAdminMailbox[];
  onRulesChange: (rules: MailFolderRule[]) => void;
  rules: MailFolderRule[];
  scope?: 'admin' | 'owner';
}) {
  const [mailboxId, setMailboxId] = useState(mailboxes[0]?.id ?? '');
  const [name, setName] = useState('Move spam to Junk');
  const [priority, setPriority] = useState(100);
  const [action, setAction] = useState<'move' | 'copy'>('move');
  const [targetFolder, setTargetFolder] = useState('Junk');
  const [stopProcessing, setStopProcessing] = useState(true);
  const [conditions, setConditions] = useState<MailFolderRuleCondition[]>([
    { field: 'classification', operator: 'equals', value: 'spam' }
  ]);
  const [editingRule, setEditingRule] = useState<MailFolderRule | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'error'>('idle');
  const [statusDetail, setStatusDetail] = useState('Rules apply to new inbound mail after address routing.');

  function updateCondition(index: number, patch: Partial<MailFolderRuleCondition>) {
    setConditions((current) => current.map((condition, candidate) => {
      if (candidate !== index) {
        return condition;
      }
      const updated = { ...condition, ...patch };
      if (patch.field) {
        updated.operator = folderRuleOperators[patch.field][0];
      }
      return updated;
    }));
  }

  function resetForm() {
    setEditingRule(null);
    setName('Move spam to Junk');
    setPriority(100);
    setAction('move');
    setTargetFolder('Junk');
    setStopProcessing(true);
    setConditions([{ field: 'classification', operator: 'equals', value: 'spam' }]);
  }

  function editRule(rule: MailFolderRule) {
    setEditingRule(rule);
    setMailboxId(rule.mailbox_id);
    setName(rule.name);
    setPriority(rule.priority);
    setAction(rule.action);
    setTargetFolder(rule.target_folder);
    setStopProcessing(rule.stop_processing);
    setConditions(rule.conditions.map((condition) => ({ ...condition })));
    setStatus('idle');
    setStatusDetail(`Editing ${rule.name} at version ${rule.version}.`);
  }

  function saveRule() {
    const input: MailFolderRuleInput = {
      mailbox_id: mailboxId,
      name: name.trim(),
      priority,
      enabled: editingRule?.enabled ?? true,
      conditions: conditions.map((condition) => ({ ...condition, value: condition.value.trim() })),
      action,
      target_folder: targetFolder.trim(),
      stop_processing: stopProcessing
    };
    if (!input.mailbox_id || !input.name || !input.target_folder || input.conditions.some((condition) => !condition.value)) {
      setStatus('error');
      setStatusDetail('Mailbox, name, target folder and every condition value are required.');
      return;
    }

    setStatus('saving');
    const request = editingRule
      ? (scope === 'owner' ? replaceSelfMailFolderRule(editingRule, input) : replaceMailFolderRule(editingRule, input))
      : (scope === 'owner' ? createSelfMailFolderRule(input) : createMailFolderRule(input));
    request
      .then((saved) => {
        onRulesChange(sortFolderRules(editingRule
          ? rules.map((candidate) => candidate.id === saved.id ? saved : candidate)
          : [...rules, saved]));
        const operation = editingRule ? 'updated' : 'created';
        resetForm();
        setStatus('idle');
        setStatusDetail(`Rule ${operation}. The ingestion worker sees it after its short cache refresh.`);
      })
      .catch(() => {
        setStatus('error');
        setStatusDetail(editingRule
          ? 'Rule changed concurrently or the store is unavailable. Reload before retrying.'
          : 'Rule was not created. Check validation, authorization and server logs.');
      });
  }

  function toggleRule(rule: MailFolderRule) {
    setStatus('saving');
    (scope === 'owner' ? replaceSelfMailFolderRule(rule, folderRuleInput(rule, !rule.enabled)) : replaceMailFolderRule(rule, folderRuleInput(rule, !rule.enabled)))
      .then((updated) => {
        onRulesChange(sortFolderRules(rules.map((candidate) => candidate.id === updated.id ? updated : candidate)));
        setStatus('idle');
        setStatusDetail(`${updated.name} is now ${updated.enabled ? 'enabled' : 'disabled'}.`);
      })
      .catch(() => {
        setStatus('error');
        setStatusDetail('Rule changed concurrently or the store is unavailable. Reload before retrying.');
      });
  }

  function removeRule(rule: MailFolderRule) {
    setStatus('saving');
    (scope === 'owner' ? deleteSelfMailFolderRule(rule) : deleteMailFolderRule(rule))
      .then(() => {
        onRulesChange(rules.filter((candidate) => candidate.id !== rule.id));
        setStatus('idle');
        setStatusDetail(`${rule.name} was deleted.`);
      })
      .catch(() => {
        setStatus('error');
        setStatusDetail('Rule changed concurrently or could not be deleted. Reload before retrying.');
      });
  }

  return (
    <section className="admin-panel folder-rule-panel" aria-label="Server-side folder rules">
      <div className="panel-heading">
        <p className="section-label">Server-side filing</p>
        <h3>Put every client in the same folder state.</h3>
        <p>Rules are ordered by priority. Conditions inside one rule use AND; move changes the primary folder, copy adds another membership.</p>
      </div>

      <div className="folder-rule-layout">
        <form className="folder-rule-form" onSubmit={(event) => { event.preventDefault(); saveRule(); }}>
          <label>
            Mailbox
            <select disabled={Boolean(editingRule)} onChange={(event) => setMailboxId(event.currentTarget.value)} value={mailboxId}>
              {mailboxes.map((mailbox, index) => <option key={mailbox.id} value={mailbox.id}>{mailboxOptionLabel(mailbox, undefined, index)}</option>)}
            </select>
            {editingRule ? <small>Mailbox is immutable while editing. Delete and recreate the rule to choose another owned mailbox.</small> : null}
          </label>
          <label>
            Rule name
            <input maxLength={120} onChange={(event) => setName(event.currentTarget.value)} value={name} />
          </label>
          <label>
            Priority
            <input max={10000} min={0} onChange={(event) => setPriority(Number(event.currentTarget.value))} type="number" value={priority} />
          </label>

          <div className="folder-rule-conditions">
            <div className="folder-rule-subhead">
              <strong>Match all conditions</strong>
              <button
                disabled={conditions.length >= 8}
                onClick={() => setConditions((current) => [...current, { field: 'subject', operator: 'contains', value: '' }])}
                type="button"
              >
                Add condition
              </button>
            </div>
            {conditions.map((condition, index) => (
              <div className="folder-rule-condition" key={`${index}-${condition.field}`}>
                <select aria-label={`Condition ${index + 1} field`} onChange={(event) => updateCondition(index, { field: event.currentTarget.value })} value={condition.field}>
                  {Object.keys(folderRuleOperators).map((field) => <option key={field} value={field}>{field.replaceAll('_', ' ')}</option>)}
                </select>
                <select aria-label={`Condition ${index + 1} operator`} onChange={(event) => updateCondition(index, { operator: event.currentTarget.value })} value={condition.operator}>
                  {folderRuleOperators[condition.field].map((operator) => <option key={operator} value={operator}>{operator.replaceAll('_', ' ')}</option>)}
                </select>
                <input aria-label={`Condition ${index + 1} value`} onChange={(event) => updateCondition(index, { value: event.currentTarget.value })} placeholder={condition.field === 'confidence' ? '0.90' : 'value'} value={condition.value} />
                <button disabled={conditions.length === 1} onClick={() => setConditions((current) => current.filter((_, candidate) => candidate !== index))} type="button">Remove</button>
              </div>
            ))}
          </div>

          <div className="folder-rule-action">
            <label>
              Action
              <select onChange={(event) => setAction(event.currentTarget.value as 'move' | 'copy')} value={action}>
                <option value="move">Move</option>
                <option value="copy">Copy</option>
              </select>
            </label>
            <label>
              Target folder
              <input maxLength={255} onChange={(event) => setTargetFolder(event.currentTarget.value)} value={targetFolder} />
            </label>
            <label className="folder-rule-check">
              <input checked={stopProcessing} onChange={(event) => setStopProcessing(event.currentTarget.checked)} type="checkbox" />
              Stop after match
            </label>
          </div>
          <div className="folder-rule-submit">
            <button className="folder-rule-primary" disabled={status === 'saving'} type="submit">
              {status === 'saving' ? 'Saving...' : editingRule ? 'Update rule' : 'Create rule'}
            </button>
            {editingRule ? <button disabled={status === 'saving'} onClick={resetForm} type="button">Cancel</button> : null}
          </div>
          <small className={status === 'error' ? 'folder-rule-status error' : 'folder-rule-status'}>{statusDetail}</small>
        </form>

        <div className="folder-rule-stack">
          {rules.length === 0 ? <p>No server-side folder rules yet.</p> : rules.map((rule) => (
            <article className={rule.enabled ? 'folder-rule-card' : 'folder-rule-card disabled'} key={rule.id}>
              <div className="folder-rule-title">
                <div>
                  <strong>{rule.name}</strong>
                  <small>{mailboxes.find((mailbox) => mailbox.id === rule.mailbox_id) ? mailboxOptionLabel(mailboxes.find((mailbox) => mailbox.id === rule.mailbox_id)!, undefined, mailboxes.findIndex((mailbox) => mailbox.id === rule.mailbox_id)) : 'Mailbox unavailable'} · priority {rule.priority} · v{rule.version}</small>
                </div>
                <span>{rule.action} → {rule.target_folder}</span>
              </div>
              <div className="folder-rule-expression">
                {rule.conditions.map((condition, index) => <code key={`${rule.id}-${index}`}>{condition.field} {condition.operator} {condition.value}</code>)}
              </div>
              <div className="folder-rule-buttons">
                <button disabled={status === 'saving'} onClick={() => editRule(rule)} type="button">Edit</button>
                <button disabled={status === 'saving'} onClick={() => toggleRule(rule)} type="button">{rule.enabled ? 'Disable' : 'Enable'}</button>
                <button className="danger" disabled={status === 'saving'} onClick={() => removeRule(rule)} type="button">Delete</button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function folderRuleInput(rule: MailFolderRule, enabled = rule.enabled): MailFolderRuleInput {
  return {
    mailbox_id: rule.mailbox_id,
    name: rule.name,
    priority: rule.priority,
    enabled,
    conditions: rule.conditions,
    action: rule.action,
    target_folder: rule.target_folder,
    stop_processing: rule.stop_processing
  };
}

function sortFolderRules(rules: MailFolderRule[]): MailFolderRule[] {
  return [...rules].sort((left, right) => left.mailbox_id.localeCompare(right.mailbox_id) || left.priority - right.priority || left.id.localeCompare(right.id));
}

function PasswordResetPanel({ account }: { account: IdentityAdminAccount }) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'partial' | 'error'>('idle');
  const ready = account.status === 'ready';
  const valid = password.length >= 14 && password === confirmation;

  function submitPasswordReset() {
    if (!ready || !valid) {
      setStatus('error');
      return;
    }

    setStatus('submitting');
    resetIdentityPassword(account.userId, password)
      .then((result) => {
        setPassword('');
        setConfirmation('');
        setStatus(result.providerSessionsRevoked ? 'success' : 'partial');
      })
      .catch(() => setStatus('error'));
  }

  return (
    <div className="password-reset-panel">
      <div className="password-reset-copy">
        <strong>Replace password</strong>
        <small>{identityStatusDetail(account)}</small>
      </div>
      <label>
        New password
        <input
          autoComplete="new-password"
          disabled={!ready || status === 'submitting'}
          minLength={14}
          onChange={(event) => setPassword(event.currentTarget.value)}
          type="password"
          value={password}
        />
      </label>
      <label>
        Confirm password
        <input
          autoComplete="new-password"
          disabled={!ready || status === 'submitting'}
          minLength={14}
          onChange={(event) => setConfirmation(event.currentTarget.value)}
          type="password"
          value={confirmation}
        />
      </label>
      <button disabled={!ready || !valid || status === 'submitting'} onClick={submitPasswordReset} type="button">
        {status === 'submitting' ? 'Replacing...' : 'Replace and revoke sessions'}
      </button>
      {status === 'success' ? <small className="password-reset-result success">Password replaced. Active sessions were revoked.</small> : null}
      {status === 'partial' ? <small className="password-reset-result partial">Password was replaced, but provider session cleanup was incomplete. Do not retry the reset.</small> : null}
      {status === 'error' ? <small className="password-reset-result error">Password was not accepted. Check identity readiness, matching values, and the server audit.</small> : null}
    </div>
  );
}

function identityStatusLabel(status: IdentityAdminAccount['status']): string {
  switch (status) {
    case 'ready': return 'LDAP ready';
    case 'missing': return 'Keycloak missing';
    case 'not_federated': return 'not LDAP-backed';
    case 'disabled': return 'disabled';
    default: return 'write path offline';
  }
}

function identityStatusDetail(account: IdentityAdminAccount): string {
  switch (account.status) {
    case 'ready': return 'Directory identity ready · 14-256 bytes · current password is never readable';
    case 'missing': return 'No exact Keycloak identity exists for this route user.';
    case 'not_federated': return 'The matching identity is local to Keycloak, so LDAP write-through is blocked.';
    case 'disabled': return 'The matching identity is disabled in Keycloak.';
    default: return 'Identity administration is disabled or not configured on the backend.';
  }
}

function AssistantResults({
  confirmations,
  onConfirm,
  results,
  status
}: {
  confirmations: Record<string, ConfirmationState>;
  onConfirm: (result: AssistantResult) => void;
  results: AssistantResult[];
  status: 'idle' | 'running' | 'error';
}) {
  if (status === 'idle' && results.length === 0) {
    return null;
  }

  return (
    <aside className="assistant-results" aria-label="Assistant results">
      <strong>{status === 'running' ? 'Searching projections...' : status === 'error' ? 'Assistant unavailable' : 'Projection results'}</strong>
      {results.map((result) => (
        <AssistantResultRow confirmation={confirmations[result.id]} key={result.id} onConfirm={onConfirm} result={result} />
      ))}
    </aside>
  );
}

function AssistantResultRow({
  confirmation,
  onConfirm,
  result
}: {
  confirmation: ConfirmationState | undefined;
  onConfirm: (result: AssistantResult) => void;
  result: AssistantResult;
}) {
  const canConfirm = Boolean(result.confirmationId) && confirmation?.status !== 'confirmed' && confirmation?.status !== 'confirming';

  return (
    <div className="assistant-result-row">
      <span>
        <b>{result.kind}</b> · {result.title} · {result.detail} · {result.sourceField}
      </span>
      {result.confirmationId ? (
        <div className="assistant-confirmation">
          <button disabled={!canConfirm} onClick={() => onConfirm(result)} type="button">
            {confirmation?.status === 'confirming' ? 'Confirming...' : confirmation?.status === 'confirmed' ? 'Confirmed' : 'Confirm draft action'}
          </button>
          <small>{confirmation?.detail ?? 'Draft-only result. Confirming routes it through the server-side action sink.'}</small>
        </div>
      ) : null}
    </div>
  );
}

function confirmationDetail(confirmation: AssistantConfirmation): string {
  return `${confirmation.intent} ${confirmation.writeStatus} via ${confirmation.sourceField}`;
}

function ComposePanel({
  body,
  fromIdentity,
  identities,
  onBodyChange,
  onFromChange,
  onSubjectChange,
  onSubmit,
  onToChange,
  status,
  subject,
  to
}: {
  body: string;
  fromIdentity: string;
  identities: { address: string; domain: string }[];
  onBodyChange: (value: string) => void;
  onFromChange: (value: string) => void;
  onSubjectChange: (value: string) => void;
  onSubmit: () => void;
  onToChange: (value: string) => void;
  status: ComposeStatus;
  subject: string;
  to: string;
}) {
  return (
    <form
      className="compose-card"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <p className="section-label">Compose stub</p>
      <label>
        <span>From</span>
        <input
          aria-label="Compose from identity"
          list="compose-send-identities"
          onChange={(event) => onFromChange(event.currentTarget.value)}
          placeholder="you@example.com"
          value={fromIdentity}
        />
        <datalist id="compose-send-identities">
          {identities.map((identity) => (
            <option key={identity.address} value={identity.address}>
              {identity.address}
            </option>
          ))}
        </datalist>
        <small>Choose a saved identity or type an address owned by your mailbox.</small>
      </label>
      <label>
        <span>To</span>
        <input
          aria-label="Compose recipient"
          onChange={(event) => onToChange(event.currentTarget.value)}
          placeholder="friend@example.net"
          value={to}
        />
      </label>
      <label>
        <span>Subject</span>
        <input aria-label="Compose subject" onChange={(event) => onSubjectChange(event.currentTarget.value)} value={subject} />
      </label>
      <label>
        <span>Body</span>
        <textarea aria-label="Compose body" onChange={(event) => onBodyChange(event.currentTarget.value)} rows={5} value={body} />
      </label>
      <button disabled={status.state === 'sending'} type="submit">
        {status.state === 'sending' ? 'Submitting...' : 'Send via stub'}
      </button>
      <small className={`compose-status ${status.state}`}>{status.detail}</small>
    </form>
  );
}

function splitAddresses(value: string): string[] {
  return value
    .split(/[;,]+/)
    .map((address) => address.trim())
    .filter(Boolean);
}

function snapshotErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return `The live mailbox API is unavailable (HTTP ${error.status}).`;
  }

  return 'The live mailbox API is unavailable. Check the connection and retry.';
}

function folderKey(mailboxId: string, folder: string): string {
  return `${mailboxId}::${folder}`;
}

function orderedFolders(folders: Record<string, string>): string[] {
  return Object.values(folders).sort((left, right) => {
    if (left === 'INBOX') {
      return -1;
    }
    if (right === 'INBOX') {
      return 1;
    }

    return left.localeCompare(right);
  });
}

function mergeMessages(
  currentMessages: MessageSummary[],
  nextMessages: MessageSummary[],
  resetScope?: { mailboxId: string; folder: string }
): MessageSummary[] {
  const scopedCurrent = resetScope
    ? currentMessages.filter((message) => message.mailboxId !== resetScope.mailboxId || message.folder !== resetScope.folder)
    : currentMessages;
  const byId = new Map(scopedCurrent.map((message) => [message.emailId, message]));
  nextMessages.forEach((message) => byId.set(message.emailId, message));

  return Array.from(byId.values());
}

function MessageRow({
  isSelected,
  message,
  onSelect
}: {
  isSelected: boolean;
  message: MessageSummary;
  onSelect: (emailId: string) => void;
}) {
  return (
    <button className={isSelected ? 'message-row active' : 'message-row'} onClick={() => onSelect(message.emailId)}>
      <span className={`label-dot ${message.finalLabel}`} aria-hidden="true" />
      <span className="row-main">
        <strong>{message.subject}</strong>
        <small>{message.from}</small>
      </span>
      <span className="confidence">{Math.round(message.finalConfidence * 100)}%</span>
    </button>
  );
}

function MessageDetail({
  detail,
  message,
  onToggleRaw,
  showRaw,
  status
}: {
  detail: MessageDetailModel | undefined;
  message: MessageSummary;
  onToggleRaw: () => void;
  showRaw: boolean;
  status: 'idle' | 'loading' | 'error';
}) {
  const resolved = detail ?? message;
  const riskSignals = [...resolved.suspiciousSignals, ...resolved.promptInjectionSignals, ...(detail?.dangerousHtmlTags ?? [])];
  const isRisky = resolved.finalLabel !== 'ham' || riskSignals.length > 0;
  const readableBody = detail?.bodyText || resolved.preview;
  const rawText = detail?.rawText || resolved.preview;

  return (
    <article>
      <p className="section-label">{isRisky && !showRaw ? 'Risk-first view' : showRaw ? 'Raw text view' : 'Message view'}</p>
      <h2>{resolved.subject}</h2>
      <dl className="metadata-grid">
        <div>
          <dt>From</dt>
          <dd>{resolved.from}</dd>
        </div>
        <div>
          <dt>Delivered to</dt>
          <dd>{resolved.deliveryRecipient}</dd>
        </div>
        <div>
          <dt>Route</dt>
          <dd>{resolved.routeType}</dd>
        </div>
        <div>
          <dt>Classifier</dt>
          <dd>{resolved.finalLabel}</dd>
        </div>
      </dl>

      <section className="preview-card">
        <div className="message-toolbar">
          <span>{detail?.bodyAvailable ? 'IMAP-backed body loaded' : status === 'loading' ? 'Loading body...' : 'Projection fallback'}</span>
          <button onClick={onToggleRaw} type="button">
            {showRaw ? 'Show message' : 'Show raw text'}
          </button>
        </div>
        {status === 'loading' ? (
          <p>Loading backend message...</p>
        ) : status === 'error' ? (
          <p>Backend message unavailable.</p>
        ) : showRaw ? (
          <pre>{rawText}</pre>
        ) : isRisky ? (
          <p>{resolved.preview}</p>
        ) : (
          <p>{readableBody}</p>
        )}
      </section>

      {!isRisky || showRaw ? <SafeHtmlFrame document={resolved.sanitizedHtml} /> : null}

      <section className="risk-card" aria-label="Why suspicious">
        <h3>{isRisky ? 'Why this needs caution' : 'Why this is safe to show'}</h3>
        <p>{isRisky ? 'Suspicious messages default to metadata and classifier evidence. Use raw text only when you need to inspect the body.' : 'Normal messages render sanitized text/HTML. Raw MIME, storage paths, and credential-bearing data stay behind the API boundary.'}</p>
        <div className="signal-list">
          {riskSignals.length > 0 ? (
            riskSignals.map((signal) => <span key={signal}>{signal}</span>)
          ) : (
            <span>No suspicious signals</span>
          )}
        </div>
      </section>
    </article>
  );
}

function SafeHtmlFrame({ document }: { document: SanitizedHtmlDocument }) {
  return (
    <section className="html-frame-card" aria-label="Sanitized HTML preview">
      <div>
        <h3>Sanitized HTML</h3>
        <p>{document.contentSecurityPolicy}</p>
      </div>
      <iframe sandbox="" srcDoc={document.html} referrerPolicy="no-referrer" title="Sanitized message HTML preview" />
    </section>
  );
}
